import { personRef, linkedPerson, resolvedSignatory } from "../../../utils/linkedScreeningPerson";
import ScreeningPendingSubject from "./ScreeningPendingSubject";
import ScreeningNameNotice from "./ScreeningNameNotice";
import { needsFullName } from "../../../utils/screeningName";
import { assessContext, overallIdentity, contextFingerprint, VERSION as CONTEXT_VERSION } from "../../../utils/identityContext";
import { orderScreeningHits } from "../../../utils/screeningHitOrder";
import IdentityContext, { ContextLimitation, MissingContextNote } from "./IdentityContext";
import { nameFields } from "../../../utils/individualName";
import { sanctionsNameStrength, sanctionsMatchReason, compareSanctionsNameMatches, sanctionsSubjectFingerprint, sanctionsScreeningFailureMessage } from "../../../utils/sanctionsNameMatch";
import { isSubjectReviewComplete, noMatchSubjectKeys, hasSubjectScreening, unchangedScreeningSubjects, retainSubjectScreening } from "../../../utils/exposureReview";
/* KycSectionSanctionsWithScreening.jsx */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";

import { auth } from "../../../firebase";
import { getSanctionsReviewState } from "../../../utils/sanctionsReview";
import userGreenIcon from "../../../images/user-d-green.svg";
import userRedIcon from "../../../images/user-d-red4.svg";
import userYellowIcon from "../../../images/user-d-yellow2.svg";
import Lottie from "lottie-react";
import pepAnim from "../../../images/sanctions-lottie.json";
import overallAlertAnim from "../../../images/alert-lottie.json";
import overallClearedAnim from "../../../images/cleared-lottie.json";
import overallPendingAnim from "../../../images/pending-lottie.json";
import doubleTapFullIcon from "../../../images/double-tap-full.svg";
import handFingerUpIcon from "../../../images/hand-fingerup.svg";
import { trackScreeningUsage } from "../../../utils/screeningUsage";
import { MYKYC_API_BASE } from "../../../config/api";

import "../css/KycSectionPepSanctionWithScreening.css";

/**
 * Staff sanctions screening: watchlist hits support an independent outcome per
 * screened subject. Review completes only when every subject has an outcome.
 * personReviews and relevantKeys are authoritative; legacy selection fields are
 * written as compatibility projections. Manual notes live in
 * sanctions.supportingInformation and survive a new screening run.
 */

function safeStr(v) {
  return String(v == null ? "" : v);
}
function clean(v) {
  return safeStr(v)
    .replace(/\uFEFF/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^"(.*)"$/, "$1");
}

const DEFAULT_SANCTIONS_SCREENING_SOURCES = ["ofac", "un"];
const ALLOWED_SANCTIONS_SCREENING_SOURCES = new Set(["ofac", "un", "uae"]);

function sanitizeSanctionsScreeningSources(value) {
  const list = Array.isArray(value) ? value : [];
  const out = [];
  list.forEach((item) => {
    const cleanSource = clean(item).toLowerCase();
    if (
      ALLOWED_SANCTIONS_SCREENING_SOURCES.has(cleanSource) &&
      !out.includes(cleanSource)
    ) {
      out.push(cleanSource);
    }
  });
  return out.length ? out : DEFAULT_SANCTIONS_SCREENING_SOURCES;
}

function autoResizeTextarea(el) {
  if (!el || typeof window === "undefined") return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight + verticalExtras;
  const maxHeight = lineHeight * 25 + verticalExtras;

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

function titleCaseWords(value) {
  return clean(value)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normKey(v) {
  return clean(v).toLowerCase();
}

function badge(text, scheme = "secondary") {
  const cls =
    scheme === "danger"
      ? "badge badgeStandard badgeKycRed"
      : scheme === "warning"
      ? "badge badgeStandard badgeKycYellow"
      : scheme === "success"
      ? "badge badgeStandard badgeKycGreen"
      : scheme === "grey"
      ? "badge badgeStandard badgeKycGrey"
      : scheme === "orange"
      ? "badge badgeStandard badgeKycOrange"
      : "badge badgeStandard badgeKycGrey";
  return <span className={cls}>{text}</span>;
}

function squaredBadge(text, scheme = "secondary") {
  const cls =
    scheme === "lightblue"
      ? "squareBadge squareBadgeLightBlue"
      : scheme === "darkblue"
      ? "squareBadge squareBadgeDarkBlue"
      : scheme === "darkblue-outline"
      ? "squareBadge squareBadgeDarkBlue-outline"
      : scheme === "grey"
      ? "squareBadge squareBadgeGrey"
      : scheme === "white"
      ? "squareBadge squareBadgeWhite"
      : "squareBadge squareBadgeGrey";
  return <span className={cls}>{text}</span>;
}

function screenedRolesText(subjects) {
  const arr = Array.isArray(subjects) ? subjects : [];
  const counts = {
    Client: 0,
    "Direct shareholder": 0,
    UBO: 0,
    "Authorised signatory": 0,
  };

  for (const s of arr) {
    const roles = Array.isArray(s?.roles) ? s.roles : [];
    const uniq = Array.from(
      new Set(roles.map((r) => clean(r)).filter(Boolean))
    );
    const lower = uniq.map((r) => r.toLowerCase());
    if (lower.some((r) => r.includes("client"))) counts.Client += 1;
    else if (lower.some((r) => r.includes("direct shareholder")))
      counts["Direct shareholder"] += 1;
    else if (
      lower.some(
        (r) =>
          r === "ubo" ||
          r.includes("ubo") ||
          r === "settlor" ||
          r === "trustee" ||
          r === "beneficiary"
      )
    )
      counts.UBO += 1;
    else if (lower.some((r) => r.includes("authorised signatory")))
      counts["Authorised signatory"] += 1;
  }

  const parts = [];
  if (counts.Client)
    parts.push(counts.Client === 1 ? "Client" : `${counts.Client} clients`);

  if (counts["Direct shareholder"])
    parts.push(
      `${counts["Direct shareholder"]} ${pluralize(
        counts["Direct shareholder"],
        "direct shareholder",
        "direct shareholders"
      )}`
    );

  if (counts.UBO)
    parts.push(`${counts.UBO} ${pluralize(counts.UBO, "UBO", "UBOs")}`);

  if (counts["Authorised signatory"])
    parts.push(
      `${counts["Authorised signatory"]} ${pluralize(
        counts["Authorised signatory"],
        "authorised signatory",
        "authorised signatories"
      )}`
    );

  return parts.join(" · ") || "Client";
}

function sourcePill(src) {
  const s = clean(src).toUpperCase();
  if (s === "UN") return badge("UN", "secondary");
  if (s === "OFAC") return badge("OFAC", "secondary");
  if (s === "UAE") return badge("UAE", "secondary");
  return badge(s || "LIST", "secondary");
}

function matchIdentityKey(m) {
  const name =
    clean(m?.matchedName) ||
    clean(m?.name) ||
    clean(m?.candidateName) ||
    clean(m?.record?.name) ||
    "Unknown";

  const src =
    clean(m?.source || m?.key || m?.list || m?.record?.source || "").toUpperCase() ||
    "LIST";

  const uid =
    clean(m?.record?.uid) ||
    clean(m?.record?.DATAID) ||
    clean(m?.record?.dataid) ||
    clean(m?.record?.id) ||
    clean(m?.uid) ||
    clean(m?.id) ||
    "";

  const subjectName = clean(m?.__subjectName || m?.subjectName || "");
  const subjectKey = clean(m?.__subjectKey || m?.subjectKey) || (subjectName ? normKey(subjectName) : "unknown_subject");

  if (m?.listingKey) return `${m.listingKey}:${subjectKey}`;
  return uid
    ? `${src}:${uid}:${subjectKey}`
    : `${src}:${name.toLowerCase()}:${subjectKey}`;
}

function buildSanctionsSelectedMatchSnapshot(match) {
  const score = Number(match?.score);
  return {
    key: matchIdentityKey(match),
    ...(match?.listingKey ? { listingKey: match.listingKey } : {}),
    name:
      clean(match?.record?.name) ||
      clean(match?.matchedName) ||
      clean(match?.name) ||
      clean(match?.candidateName) ||
      clean(match?.record?.name) ||
      "Unknown",
    source:
      clean(
        match?.source || match?.key || match?.list || match?.record?.source || ""
      ).toUpperCase() || "LIST",
    score: Number.isFinite(score) ? score : null,
    strength: sanctionsNameStrength(match),
    matchType: clean(match?.matchType || match?.reason || match?.type || ""),
    matchReason: clean(match?.matchReason || ""),
    matchedName: clean(match?.matchedName || ""),
    matchedNameKind: clean(match?.matchedNameKind || ""),
    record: match?.record || match,
    subjectName: clean(match?.__subjectName || match?.subjectName || ""),
    subjectKey: clean(match?.__subjectKey || match?.subjectKey || ""),
    roles: Array.isArray(match?.__roles) ? match.__roles : [],
    subjectMeta: match?.__subjectMeta || {},
    contextAssessment: match?.contextAssessment || defaultContextAssessment(),
  };
}

function overallMatchAssessment(strength, context) { return overallIdentity(strength, context); }

function defaultContextAssessment() {
  return {
    code: "no_context",
    label: "No context",
    checks: {
      nationality: "not_comparable",
      domicile: "not_comparable",
      age: "not_comparable",
    },
  };
}





async function fetchSanctionsContextAssessment({ subject, match }) {
  return assessContext(subject?.meta || {}, match?.record || match, "sanctions");
}

// background color of the cards above the hits
function toneStyles({ hasResult, hasMatches }) {
  if (!hasResult) return { bg: "#EDEAE9", border: "#ffd7bf", text: "#4C5465" };
  if (!hasMatches) return { bg: "#EDEAE9", border: "#ffd7bf", text: "#4D5465" };
  return { bg: "#EDEAE9", border: "#ffd7bf", text: "#4D5465" };
}
// function toneStyles({ hasResult, hasMatches }) {
//   if (!hasResult) return { bg: "#F3F4F7", border: "#ffd7bf", text: "#4C5465" };
//   if (!hasMatches) return { bg: "#F3F4F7", border: "#ffd7bf", text: "#4D5465" };
//   return { bg: "#F3F4F7", border: "#ffd7bf", text: "#4D5465" };
// }

async function asyncPool(limit, items, iteratorFn) {
  const ret = [];
  const executing = [];
  for (const item of items) {
    const p = Promise.resolve().then(() => iteratorFn(item));
    ret.push(p);
    if (limit <= items.length) {
      const e = p.then(() => executing.splice(executing.indexOf(e), 1));
      executing.push(e);
      if (executing.length >= limit) await Promise.race(executing);
    }
  }
  return Promise.all(ret);
}

function pluralize(n, singular, plural) {
  return n === 1 ? singular : plural;
}

function canonicalRoleLabel(roleRaw) {
  const r = clean(roleRaw).toLowerCase();
  if (!r) return "";
  if (r.includes("client")) return "Client";
  if (r.includes("direct shareholder")) return "Direct shareholder";
  if (r === "ubo" || r.includes("ubo")) return "UBO";
  if (r.includes("authorised signatory")) return "Authorised signatory";
  return clean(roleRaw);
}

function subjectCategoryKey(roleRaw) {
  const r = clean(roleRaw).toLowerCase();
  if (!r) return "";
  if (r.includes("client")) return "client";
  if (r.includes("direct shareholder")) return "direct_shareholder";
  if (r === "ubo" || r.includes("ubo")) return "ubo";
  if (r.includes("authorised signatory")) return "authorised_signatory";
  return "other";
}

function roleBadge(roleRaw) {
  const label = canonicalRoleLabel(roleRaw) || "Role";
  const scheme = "darkblue-outline"; // keep consistent / neutral
  return squaredBadge(label, scheme);
}

const SHOW_NO_EXPOSURE_CARD = false;

const T = {
  fast: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
  med: { duration: 0.26, ease: [0.22, 1, 0.36, 1] },
};

const cardIn = {
  initial: { opacity: 0, y: 10, scale: 0.995 },
  animate: { opacity: 1, y: 0, scale: 1, transition: T.med },
  exit: { opacity: 0, y: 8, scale: 0.995, transition: T.fast },
};

const detailsIn = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto", transition: T.med },
  exit: { opacity: 0, height: 0, transition: T.fast },
};

const fullDetailsExit = {
  overflow: "hidden",
  opacity: 0,
  height: 0,
  marginTop: 0,
  transition: {
    opacity: { duration: 0.12, ease: "easeOut" },
    height: { duration: 0.24, delay: 0.06, ease: [0.22, 1, 0.36, 1] },
    marginTop: { duration: 0.24, delay: 0.06, ease: [0.22, 1, 0.36, 1] },
  },
};

// Animate the clipped space rather than scaling the evidence or its text.
// Start closing after the fade begins, with a short overlap between the two.
const evidenceCollapse = {
  initial: { opacity: 0, height: 0 },
  animate: {
    opacity: 1,
    height: "auto",
    transition: {
      height: { duration: 0.2, ease: "easeOut" },
      opacity: { duration: 0.14, delay: 0.06, ease: "easeOut" },
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      opacity: { duration: 0.14, ease: "easeOut" },
      height: { duration: 0.2, delay: 0.08, ease: "easeInOut" },
    },
  },
};

const KycSectionSanctionsWithScreening = forwardRef(function KycSectionSanctionsWithScreening({
  sectionNo = 0,
  form,
  onSetAt,
  setAt,
  changedClass,
  readOnly = false,
  clientLocked = true,
  initiallyExpanded = false,
  onScreeningStateChange,
  hideScreeningSummary = true,
  sanctionsSources = DEFAULT_SANCTIONS_SCREENING_SOURCES,
}, ref) {
  const writeAt = typeof onSetAt === "function" ? onSetAt : setAt;
  const effectiveSanctionsSources = useMemo(
    () => sanitizeSanctionsScreeningSources(sanctionsSources),
    [sanctionsSources]
  );

  const sanctions = form?.sanctions || {};
  const cc = (path) =>
    typeof changedClass === "function" ? changedClass(path) : "";

  const customerName = useMemo(
    () => String(form?.customer?.name || "").trim(),
    [form?.customer?.name]
  );

  const ownership = useMemo(() => form?.ownership || {}, [form?.ownership]);

  const isEntityClient = useMemo(() => {
    const t = clean(
      form?.customer?.type ||
        form?.customer?.customerType ||
        form?.customer?.clientType ||
        form?.customer?.profileType ||
        ""
    ).toLowerCase();

    if (
      t.includes("entity") ||
      t.includes("company") ||
      t.includes("corporate")
    )
      return true;
    if (form?.customer?.isEntity === true) return true;

    const anyOwnership =
      (Array.isArray(ownership?.shareholders) &&
        ownership.shareholders.length > 0) ||
      (Array.isArray(ownership?.ubos) && ownership.ubos.length > 0) ||
      (Array.isArray(ownership?.signatories) &&
        ownership.signatories.length > 0) ||
      (Array.isArray(ownership?.authorisedSignatories) &&
        ownership.authorisedSignatories.length > 0) ||
      (Array.isArray(ownership?.signatories) &&
        ownership.signatories.length > 0) ||
      (Array.isArray(ownership?.directors) && ownership.directors.length > 0);

    return !!anyOwnership;
  }, [form?.customer, ownership]);

  const sanctionsContextInputSig = JSON.stringify({
    customer: {
      name: clean(form?.customer?.name || ""),
      type: clean(
        form?.customer?.type ||
          form?.customer?.customerType ||
          form?.customer?.clientType ||
          form?.customer?.profileType ||
          ""
      ),
      isEntity: form?.customer?.isEntity === true,
      ...nameFields(form?.customer),
        nationality: clean(form?.customer?.nationalityOrIncorporationCountry || ""),
      domicileCountry: clean(form?.customer?.country || ""),
      dateOfBirth: safeStr(form?.customer?.dobOrIncorporationDate || ""),
    },
    ownership: {
      shareholders: (
        Array.isArray(ownership?.shareholders)
          ? ownership.shareholders
          : Array.isArray(ownership?.directShareholders)
          ? ownership.directShareholders
          : []
      ).map((r) => ({
        name: clean(r?.name || ""),
        holderType: clean(r?.holderType || ""),
        ...nameFields(r),
        nationality: clean(r?.nationality || ""),
        domicileCountry: clean(r?.countryOfResidence || r?.domicile || ""),
        dateOfBirth: safeStr(r?.dateOfBirth || r?.dob || ""),
      })),
      ubos: (Array.isArray(ownership?.ubos) ? ownership.ubos : []).map((r) => ({
        name: clean(r?.name || ""),
        ...nameFields(r),
        nationality: clean(r?.nationality || ""),
        domicileCountry: clean(r?.countryOfResidence || r?.domicile || ""),
        dateOfBirth: safeStr(r?.dateOfBirth || r?.dob || ""),
      })),
      personLinks: (ownership?.signatories || []).map(row => row._personLink || null),
      personRowIds: [ownership?.shareholders || [], ownership?.ubos || [], ownership?.signatories || []].map(rows => rows.map(row => row._rowId || "")),
      signatories: (
        Array.isArray(ownership?.signatories)
          ? ownership.signatories
          : Array.isArray(ownership?.authorisedSignatories)
          ? ownership.authorisedSignatories
          : Array.isArray(ownership?.directors)
          ? ownership.directors
          : []
      ).map((r) => ({
        name: clean(r?.name || ""),
        capacity: clean(r?.capacity || r?.role || r?.title || ""),
        ...nameFields(r),
        nationality: clean(r?.nationality || ""),
        domicileCountry: clean(r?.countryOfResidence || r?.domicile || ""),
        dateOfBirth: safeStr(r?.dateOfBirth || r?.dob || ""),
      })),
      legalArrangements: (
        Array.isArray(ownership?.legalArrangements?.arrangements)
          ? ownership.legalArrangements.arrangements
          : []
      ).map((arr) => ({
        arrangementType: clean(arr?.arrangementType || arr?.type || ""),
        source: clean(arr?.source || ""),
        parties: (Array.isArray(arr?.parties) ? arr.parties : []).map((p) => ({
          name: clean(p?.name || ""),
          role: clean(p?.role || ""),
          trusteeKind: clean(p?.trusteeKind || ""),
          ...nameFields(p),
          nationality: clean(p?.nationality || ""),
          domicileCountry: clean(p?.domicile || p?.countryOfResidence || ""),
          dateOfBirth: safeStr(p?.dateOfBirth || p?.dob || ""),
        })),
      })),
    },
  });

  const allScreeningSubjects = useMemo(() => {
    const map = new Map();

    function upsert(name, role, meta = {}) {
      const nm = clean(name);
      if (!nm) return;
      const baseKey = normKey(nm);
      const linkedEntry = meta.personRef && [...map.entries()].find(([, item]) => item.meta?.personRef === meta.personRef);
      if (linkedEntry) {
        if (!linkedEntry[1].roles.includes(role)) linkedEntry[1].roles.push(role);
        return;
      }
      const first = map.get(baseKey);
      const fingerprint = sanctionsSubjectFingerprint(meta);
      const key = meta.separatePerson && first ? `${baseKey}|${meta.personRef}`
        : first && sanctionsSubjectFingerprint(first.meta) !== fingerprint ? `${baseKey}|${fingerprint}` : baseKey;
      const prev = map.get(key);
      if (!prev) {
        map.set(key, { key, name: nm, roles: [role], meta });
      } else {
        if (!prev.roles.includes(role)) prev.roles.push(role);
        prev.meta = { ...(prev.meta || {}), ...(meta || {}) };
      }
    }

    if (customerName) {
      upsert(customerName, "Client", {
        subjectType: isEntityClient ? "entity" : "individual",
        ...nameFields(form?.customer),
        nationality: clean(form?.customer?.nationalityOrIncorporationCountry || ""),
        domicileCountry: clean(form?.customer?.country || ""),
        dateOfBirth: form?.customer?.dobOrIncorporationDate || "",
      });
    }

    if (isEntityClient) {
      const directShareholders = Array.isArray(ownership?.shareholders)
        ? ownership.shareholders
        : Array.isArray(ownership?.directShareholders)
        ? ownership.directShareholders
        : [];

      const ubos = Array.isArray(ownership?.ubos) ? ownership.ubos : [];
      const legalArrangements = Array.isArray(
        ownership?.legalArrangements?.arrangements
      )
        ? ownership.legalArrangements.arrangements
        : [];

      const controllers = Array.isArray(ownership?.signatories)
        ? ownership.signatories
        : Array.isArray(ownership?.authorisedSignatories)
        ? ownership.authorisedSignatories
        : Array.isArray(ownership?.signatories)
        ? ownership.signatories
        : Array.isArray(ownership?.directors)
        ? ownership.directors
        : [];

      for (const [idx, r] of directShareholders.entries()) {
        const holderType = clean(r?.holderType || "").toLowerCase();
        upsert(r?.name, "Direct shareholder", {
          personRef: personRef("shareholders", r, idx),
          subjectType:
            holderType === "entity" ||
            holderType === "company" ||
            holderType === "trust" ||
            holderType === "foundation"
              ? "entity"
              : "individual",
          ...nameFields(r),
          nationality: clean(r?.nationality || ""),
          domicileCountry: clean(r?.countryOfResidence || r?.domicile || ""),
          dateOfBirth: r?.dateOfBirth || r?.dob || "",
        });
      }

      for (const [idx, r] of ubos.entries()) {
        upsert(r?.name, "UBO", {
          personRef: personRef("ubos", r, idx),
          subjectType: "individual",
          ...nameFields(r),
          nationality: clean(r?.nationality || ""),
          domicileCountry: clean(r?.countryOfResidence || r?.domicile || ""),
          dateOfBirth: r?.dateOfBirth || r?.dob || "",
        });
      }

      for (const [idx, stored] of controllers.entries()) {
        const r = resolvedSignatory(ownership, stored);
        const linked = linkedPerson(ownership, stored);
        const cap = clean(r?.capacity || r?.role || r?.title || "");
        upsert(
          r?.name,
          cap ? `Authorised signatory (${cap})` : "Authorised signatory",
          {
            personRef: linked?.ref || personRef("signatories", stored, idx),
            separatePerson: !linked,
            subjectType: "individual",
            ...nameFields(r),
            nationality: clean(r?.nationality || ""),
            domicileCountry: clean(r?.countryOfResidence || r?.domicile || ""),
            dateOfBirth: r?.dateOfBirth || r?.dob || "",
          }
        );
      }

      const accountHolderTrust = legalArrangements.find((arr) => {
        const type = clean(arr?.arrangementType || arr?.type).toLowerCase();
        const source = clean(arr?.source).toLowerCase();
        return type === "trust" && source === "account_holder";
      });

      if (accountHolderTrust) {
        const parties = Array.isArray(accountHolderTrust?.parties)
          ? accountHolderTrust.parties
          : [];

        for (const p of parties) {
          const nm = clean(p?.name);
          const role = clean(p?.role).toLowerCase();
          if (!nm || !role) continue;
          if (/^class\b/i.test(nm) || /\bunnamed\b/i.test(nm)) continue;

          if (role === "trustee") {
            const trusteeKind = clean(p?.trusteeKind).toLowerCase();
            if (trusteeKind === "company" || trusteeKind === "corporate")
              continue;
            upsert(nm, "Trustee", {
              subjectType: "individual",
              ...nameFields(p),
              nationality: clean(p?.nationality || ""),
              domicileCountry: clean(p?.domicile || p?.countryOfResidence || ""),
              dateOfBirth: p?.dateOfBirth || p?.dob || "",
            });
            continue;
          }

          if (role === "settlor" || role === "beneficiary") {
            upsert(nm, role === "settlor" ? "Settlor" : "Beneficiary", {
              subjectType: "individual",
              ...nameFields(p),
              nationality: clean(p?.nationality || ""),
              domicileCountry: clean(p?.domicile || p?.countryOfResidence || ""),
              dateOfBirth: p?.dateOfBirth || p?.dob || "",
            });
          }
        }
      }
    }

    const out = Array.from(map.values());
    out.sort((a, b) => {
      const aC = a.roles.includes("Client") ? 0 : 1;
      const bC = b.roles.includes("Client") ? 0 : 1;
      if (aC !== bC) return aC - bC;
      return a.name.localeCompare(b.name);
    });
    return out;
  }, [
    customerName,
    form?.customer,
    isEntityClient,
    ownership,
    sanctionsContextInputSig,
  ]);

  const blockedSubjects = useMemo(() => allScreeningSubjects.filter(needsFullName), [allScreeningSubjects]);
  const screeningSubjects = useMemo(() => allScreeningSubjects.filter(subject => !needsFullName(subject)), [allScreeningSubjects]);

  const subjectsSig = useMemo(() => {
    const parts = allScreeningSubjects
      .map((s) => ({
        n: normKey(s.name),
        r: (Array.isArray(s.roles) ? s.roles : []).slice().sort(),
        m: s.meta || {},
      }))
      .sort((a, b) => a.n.localeCompare(b.n));
    return JSON.stringify(parts);
  }, [allScreeningSubjects]);

  const subjectSignature = JSON.stringify(allScreeningSubjects.map(s => [s.key, s.roles, nameFields(s.meta), s.meta?.dateOfBirth, s.meta?.nationality, s.meta?.domicileCountry]));
  const autoClearRunRef = useRef(null);
  const screeningRunTokenRef = useRef(0);
  const previousSubjectsRef = useRef(screeningSubjects);
  const [runningSubjectKey, setRunningSubjectKey] = useState(null);
  const [screeningLoading, setScreeningLoading] = useState(false);
  const [screeningErr, setScreeningErr] = useState("");
  const [screeningResult, setScreeningResult] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [sanctionsReviewScrolled, setSanctionsReviewScrolled] = useState(false);
  const clientCommentsRef = useRef(null);
  const complianceCommentsRef = useRef(null);
  const hasResult = !!screeningResult;
  const hasScreeningErrors = !!screeningResult?.errors?.length;
  function resetComplianceAssessment() {
    if (typeof writeAt !== "function") return;
    writeAt(["sanctions", "staffDecision"], null);
    writeAt(["sanctions", "staffAssessment"], "");
  }

  function persistScreeningState(partial, notes = sanctions?.supportingInformation) {
    if (typeof writeAt !== "function") return;
    const saved = form?.internalUse?.sanctionsScreening || {};
    const next = {
      ...saved,
      schemaVersion: 2,
      subjectsSig,
      searchedName: customerName || "",
      searchedSubjects: screeningSubjects || [],
      subjectSignature: subjectSignature || "",
      runAt: saved.runAt ?? null,
      result: saved.result ?? null,
      personReviews: personDecisions,
      relevantKeys: Array.from(relevantKeys),
      ...(partial || {}),
    };
    const reviewed = !blockedSubjects.length && screeningSubjects.length > 0 && screeningSubjects.every(
      subject => isSubjectReviewed(subject.key, next.personReviews, next.relevantKeys, notes)
    );
    next.reviewStatus = reviewed ? "completed" : null;
    next.selectionSaved = reviewed;
    next.selectedKeys = next.relevantKeys;
    next.selectedMatches = (next.result?.matches || [])
      .filter(match => next.relevantKeys.includes(matchIdentityKey(match)))
      .map(buildSanctionsSelectedMatchSnapshot);
    // A transiently-null component state must never wipe the saved result.
    if (next.result == null && saved.result != null) next.result = saved.result;
    writeAt(["internalUse", "sanctionsScreening"], next);
  }

  // --- Toggle visibibility details section ---
  // Show/hide everything below the TOP SUMMARY CARD - true : visible, false: hidden
  const [showDetails, setShowDetails] = useState(!!initiallyExpanded);
  // Bis layout port: extensions row is clipped while its height animates, then
  // released so the middle arrow can overhang into the screening card below.
  const [extRevealed, setExtRevealed] = useState(!!initiallyExpanded);
  const [complianceAssessmentGlowActive, setComplianceAssessmentGlowActive] =
    useState(false);
  const [
    complianceAssessmentIntroBackgroundActive,
    setComplianceAssessmentIntroBackgroundActive,
  ] = useState(false);
  const [actionRequiredBadgeVisible, setActionRequiredBadgeVisible] =
    useState(true);
  const actionRequiredBadgeRevealTimerRef = useRef(null);
  const [collapseReviewSnapshot, setCollapseReviewSnapshot] = useState(null);
  const collapseScrollPartsRef = useRef(null);
  const collapseScrollFrameRef = useRef(null);

  function cancelDetailsCollapse() {
    window.cancelAnimationFrame(collapseScrollFrameRef.current);
    collapseScrollPartsRef.current = null;
    setCollapseReviewSnapshot(null);
  }

  function toggleDetails() {
    cancelDetailsCollapse();
    setShowDetails((v) => !v);
  }

  function finishCollapseScroll(part) {
    const pending = collapseScrollPartsRef.current;
    if (!pending) return;
    pending.delete(part);
    if (pending.size) return;
    collapseScrollPartsRef.current = null;
    setCollapseReviewSnapshot(null);
  }

  function scrollSanctionsAnchorThenCollapse() {
    window.cancelAnimationFrame(collapseScrollFrameRef.current);
    // Freeze evidence visibility while the new outcome appears in the header.
    // Otherwise the individual hit exits compete with the page scroll.
    setCollapseReviewSnapshot({ personDecisions, decisionEditKeys });
    collapseScrollPartsRef.current = new Set([
      "extensions",
      ...(hasResult || screeningLoading ? ["results"] : []),
    ]);
    collapseScrollFrameRef.current = window.requestAnimationFrame(() => {
      const anchor = document.getElementById("kyc-sec-sanctions");
      if (!anchor || anchor.getBoundingClientRect().top >= 0) {
        setShowDetails(false);
        return;
      }
      anchor.scrollIntoView({ behavior: "smooth", block: "start" });
      const startedAt = performance.now();
      let lastTop = anchor.getBoundingClientRect().top;
      let stableFrames = 0;
      const waitForScroll = (now) => {
        const top = anchor.getBoundingClientRect().top;
        stableFrames = Math.abs(top - lastTop) < 0.5 ? stableFrames + 1 : 0;
        lastTop = top;
        // Follow actual scroll completion, including reduced-motion or clamped
        // scrolling, instead of closing midway through a fixed 500ms timeout.
        if (Math.abs(top) <= 1 || (now - startedAt > 120 && stableFrames >= 3) || now - startedAt > 2000) {
          setShowDetails(false);
          return;
        }
        collapseScrollFrameRef.current = window.requestAnimationFrame(waitForScroll);
      };
      collapseScrollFrameRef.current = window.requestAnimationFrame(waitForScroll);
    });
  }

  useEffect(() => {
    return () => {
      window.cancelAnimationFrame(collapseScrollFrameRef.current);
      if (actionRequiredBadgeRevealTimerRef.current) {
        window.clearTimeout(actionRequiredBadgeRevealTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      autoResizeTextarea(complianceCommentsRef.current);
    });
  }, [sanctions?.staffAssessment]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      autoResizeTextarea(clientCommentsRef.current);
    });
  }, [sanctions?.clientDetails]);

  useEffect(() => {
    const saved = form?.internalUse?.sanctionsScreening;
    const normalized = getSanctionsReviewState(form, screeningSubjects, matchIdentityKey);
    setScreeningResult(saved?.result || null);
    setPersonDecisions(normalized.personReviews);
    setRelevantKeys(new Set(normalized.relevantKeys));
  }, [form?.internalUse?.sanctionsScreening, subjectSignature]);

  const contextRefreshRef = useRef("");
  useEffect(() => {
    if (readOnly || screeningLoading) return;
    if (prevSigRef.current !== subjectSignature) return;
    if (!screeningResult || !Array.isArray(screeningResult?.matches)) return;
    if (!screeningResult.matches.length) return;
    if (!subjectsSig) return;

    const saved = form?.internalUse?.sanctionsScreening || null;
    const refreshKey = `${CONTEXT_VERSION}:${subjectsSig}`;
    if (contextRefreshRef.current === refreshKey || saved?.contextSubjectsSig === refreshKey || screeningResult.contextSubjectsSig === refreshKey) return;

    let cancelled = false;

    async function refreshSanctionsContextOnly() {
      const subjectByName = new Map(
        (screeningSubjects || [])
          .map((subject) => [subject.key || normKey(subject?.name || ""), subject])
          .filter(([key]) => key)
      );
      const onlySubject =
        (screeningSubjects || []).length === 1 ? screeningSubjects[0] : null;

      const updatedMatches = await asyncPool(
        4,
        screeningResult.matches,
        async (match) => {
          const subjectName = clean(match?.__subjectName || match?.subjectName || "");
          const subject =
            subjectByName.get(match?.__subjectKey || match?.subjectKey || normKey(subjectName)) ||
            onlySubject ||
            null;
          if (!subject) return match;

          const contextAssessment = await fetchSanctionsContextAssessment({
            subject,
            match,
          });

          return {
            ...match,
            __roles: Array.isArray(subject?.roles) ? subject.roles : match?.__roles,
            __subjectMeta: subject?.meta || {},
            contextAssessment,
            __contextRefreshed: true,
          };
        }
      );

      if (cancelled) return;
      const refreshedCount = updatedMatches.filter((m) => m?.__contextRefreshed).length;
      if (!refreshedCount) return;

      contextRefreshRef.current = refreshKey;

      const updatedResult = {
        ...screeningResult,
        subjects: (screeningResult.subjects || []).map(subject =>
          screeningSubjects.find(current => current.key === subject.key) || subject),
        subjectsSig: screeningResult?.subjectsSig || saved?.subjectsSig || "",
        contextSubjectsSig: refreshKey,
        matches: updatedMatches,
      };

      setScreeningResult(updatedResult);
      persistScreeningState({ result: updatedResult, contextSubjectsSig: refreshKey });
    }

    refreshSanctionsContextOnly().catch(() => {
      contextRefreshRef.current = "";
    });

    return () => {
      cancelled = true;
    };
  }, [
    customerName,
    form?.internalUse?.sanctionsScreening,
    readOnly,
    screeningLoading,
    screeningResult,
    screeningSubjects,
    subjectsSig,
    subjectSignature,
    writeAt,
  ]);

  // ---- Person-grouped review (schema v2) ----
  // personDecisions mirrors internalUse.sanctionsScreening.personReviews:
  // { [subjectKey]: { decision, decidedAt, decidedByUid } }. relevantKeys
  // mirrors .relevantKeys. Legacy selectedKeys/reviewStatus no longer exist
  // (fresh-data project decision, 23 Sep).
  const [personDecisions, setPersonDecisions] = useState({});
  // Groups temporarily reopened via the chip's "Change" affordance.
  const [decisionEditKeys, setDecisionEditKeys] = useState(() => new Set());
  const [relevantKeys, setRelevantKeys] = useState(() => new Set());

  function openDecisionEdit(groupKey) {
    if (readOnly) return;
    cancelDetailsCollapse();
    setDecisionEditKeys((prev) => {
      const next = new Set(prev);
      next.add(groupKey);
      return next;
    });
  }

  function setPersonDecision(groupKey, decision) {
    if (readOnly || (decision === "no_exposure" && subjectHasSelectedEvidence(groupKey))) return;
    cancelDetailsCollapse();
    // Either option confirms the review; the separate clear button resets it.
    const outcomeChanged = personDecisions[groupKey]?.decision !== decision;
    const next = { ...personDecisions };
    if (decision == null) delete next[groupKey];
    else next[groupKey] = {
      decision,
      contextFingerprint: contextFingerprint(screeningSubjects.find(s => s.key === groupKey)?.meta),
      contextRuleVersion: CONTEXT_VERSION,
      decidedAt: new Date().toISOString(),
      decidedByUid: auth?.currentUser?.uid || "",
    };
    setPersonDecisions(next);
    setDecisionEditKeys((prev) => {
      const nextKeys = new Set(prev);
      nextKeys.delete(groupKey);
      return nextKeys;
    });
    persistScreeningState({ personReviews: next });

    updateReviewCompletion(next, relevantKeys, sanctions?.supportingInformation, outcomeChanged);
  }

  function updateReviewCompletion(next, selectedKeys, notes, outcomeChanged = false) {
    const allSubjectsReviewed = !blockedSubjects.length && screeningSubjects.length > 0 && screeningSubjects.every(
      (subject) => isSubjectReviewed(subject.key, next, selectedKeys, notes)
    );
    if (!outcomeChanged && allSubjectsReviewed === isReviewCompleted) return;
    if (actionRequiredBadgeRevealTimerRef.current) {
      window.clearTimeout(actionRequiredBadgeRevealTimerRef.current);
      actionRequiredBadgeRevealTimerRef.current = null;
    }
    if (!allSubjectsReviewed && typeof writeAt === "function") {
      writeAt(["sanctions", "staffDecision"], null);
    }
    const exposureIdentified = screeningSubjects.some(
      (subject) => next[subject.key]?.decision === "exposure"
    );
    if (typeof writeAt === "function") {
      writeAt(["sanctions", "staffDecision"], allSubjectsReviewed
        ? exposureIdentified ? "flagged" : "no_exposure"
        : null);
    }
    if (allSubjectsReviewed && !exposureIdentified) {
      scrollSanctionsAnchorThenCollapse();
    }
    if (allSubjectsReviewed && exposureIdentified) {
      setActionRequiredBadgeVisible(false);
      setComplianceAssessmentIntroBackgroundActive(true);
      setComplianceAssessmentGlowActive(true);
      actionRequiredBadgeRevealTimerRef.current = window.setTimeout(() => {
        setActionRequiredBadgeVisible(true);
        actionRequiredBadgeRevealTimerRef.current = null;
      }, 720);
    } else {
      setActionRequiredBadgeVisible(true);
      setComplianceAssessmentIntroBackgroundActive(false);
      setComplianceAssessmentGlowActive(false);
    }
  }

  function toggleRelevant(evidenceKey) {
    if (readOnly) return;
    const next = new Set(relevantKeys);
    if (next.has(evidenceKey)) next.delete(evidenceKey);
    else next.add(evidenceKey);
    setRelevantKeys(next);
    const group = groupedSanctionsReviewItems.find(item =>
      item.matches.some(match => match._key === evidenceKey)
    );
    const clearOutcome = next.has(evidenceKey) && personDecisions[group?.key]?.decision === "no_exposure";
    const nextDecisions = { ...personDecisions };
    if (clearOutcome) {
      cancelDetailsCollapse();
      delete nextDecisions[group.key];
      setPersonDecisions(nextDecisions);
    }
    persistScreeningState({ relevantKeys: Array.from(next), personReviews: nextDecisions });
    updateReviewCompletion(nextDecisions, next, sanctions?.supportingInformation, clearOutcome);
  }

  function setSupportingInformation(groupKey, value) {
    if (readOnly) return;
    const notes = { ...sanctions?.supportingInformation, [groupKey]: value };
    writeAt?.(["sanctions", "supportingInformation", groupKey], value);
    persistScreeningState({}, notes);
    updateReviewCompletion(personDecisions, relevantKeys, notes);
  }


  // ---------------- RESET SCREENING + COMPLIANCE IF SUBJECTS CHANGE ----------------
  const prevSigRef = useRef(subjectSignature);
  useEffect(() => {
    const prev = prevSigRef.current;
    if (prev !== subjectSignature) {
      screeningRunTokenRef.current += 1;
      autoClearRunRef.current = null;
      setScreeningLoading(false);
      setRunningSubjectKey(null);
      const retainedSubjects = unchangedScreeningSubjects(previousSubjectsRef.current, screeningSubjects);
      previousSubjectsRef.current = screeningSubjects;
      const retainedKeys = new Set(retainedSubjects.map(subject => subject.key));
      const retainedResult = retainSubjectScreening(screeningResult, retainedSubjects);
      const retainedReviews = Object.fromEntries(Object.entries(personDecisions).filter(([key]) => retainedKeys.has(key)));
      const evidenceKeys = new Set(groupedSanctionsReviewItems.filter(group => retainedKeys.has(group.key)).flatMap(group => [
        ...group.matches.map(match => match._key), ...(group.aiItems || []).map(item => item.evidenceKey),
      ]));
      const retainedSelections = [...relevantKeys].filter(key => evidenceKeys.has(key));
      setScreeningErr("");
      if (retainedResult) { retainedResult.subjectsSig = subjectsSig; retainedResult.contextSubjectsSig = subjectsSig; }
      setScreeningResult(retainedResult);
      cancelDetailsCollapse();
      setDecisionEditKeys(new Set());
      setOpenKey(null);
      setPersonDecisions(retainedReviews);
      setRelevantKeys(new Set(retainedSelections));

      if (typeof writeAt === "function") {
        writeAt(["internalUse", "sanctionsScreening"], retainedResult ? {
          ...form?.internalUse?.sanctionsScreening, result: retainedResult, searchedSubjects: retainedSubjects,
          subjectSignature, personReviews: retainedReviews, relevantKeys: retainedSelections,
          reviewStatus: null, selectionSaved: false,
          subjectsSig, contextSubjectsSig: subjectsSig,
        } : null);
      }

      resetComplianceAssessment();
      prevSigRef.current = subjectSignature;
    }
  }, [subjectSignature, writeAt]);

  async function runSanctionsScreening(targetSubject = null) {
    if (readOnly || screeningLoading) return;
    const target = targetSubject?.key ? screeningSubjects.find(subject => subject.key === targetSubject.key) : null;
    if (targetSubject?.key && (!target || hasSubjectScreening(screeningResult, target))) return;
    const subjectsToRun = target ? [target] : screeningSubjects;
    const keptReviews = target ? personDecisions : {};
    const keptSelections = target ? Array.from(relevantKeys) : [];
    setRunningSubjectKey(target?.key || null);
    const runToken = ++screeningRunTokenRef.current;
    autoClearRunRef.current = null;
    cancelDetailsCollapse();
    setShowDetails(true);
    if (!target) setDecisionEditKeys(new Set());
    if (!target) setPersonDecisions({});
    if (!target) setRelevantKeys(new Set());
    setScreeningErr("");
    if (!target) setScreeningResult(null);
    setOpenKey(null);

    if (typeof writeAt === "function") {
      if (!target) writeAt(["internalUse", "sanctionsScreening"], null);
    }
    resetComplianceAssessment();

    if (!customerName) {
      setScreeningErr("Missing customer name.");
      return;
    }

    const subjects = subjectsToRun || [];
    if (!subjects.length) {
      setScreeningErr(blockedSubjects.length ? "" : "No names available for screening.");
      return;
    }

    setScreeningLoading(true);
    try {
      const endpoint = `${MYKYC_API_BASE}/myKyc/screening/sanctions_screen`;
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Sign in to run screening");

      const perSubject = await asyncPool(4, subjects, async (subj) => {
        const name = clean(subj?.name);
        const roles = Array.isArray(subj?.roles) ? subj.roles : [];
        if (!name) return { ok: false, name: "", roles, error: "Missing name" };

        try {
          const res = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              name,
              ...nameFields(subj.meta),
              orgId: form?.orgId,
              subjectType: subj?.meta?.subjectType || "individual",
              sources: effectiveSanctionsSources,
            }),
          });

          const json = await res.json().catch(() => ({}));
          if (!res.ok || json?.ok === false) {
            throw Object.assign(new Error(json?.error || json?.message || "Screening failed"), { code: json?.code });
          }

          return { ok: true, name, roles, subject: subj, result: json };
        } catch (e) {
          return {
            ok: false,
            name,
            roles,
            subject: subj,
            error: e?.message || "Screening failed",
            code: e?.code || "screening_unavailable",
          };
        }
      });

      await trackScreeningUsage({
        form,
        screeningType: "sanctions",
        namesCount: subjects.length,
        subjects,
      });

      const allMatches = [];
      for (const r of perSubject) {
        if (runToken !== screeningRunTokenRef.current) return;
        const matches = Array.isArray(r?.result?.matches)
          ? r.result.matches
          : [];
        const matchesWithContext = await asyncPool(4, matches, async (m) => ({
          ...m,
          contextAssessment: await fetchSanctionsContextAssessment({
            subject: r?.subject || { name: r.name, roles: r.roles },
            match: m,
          }),
        }));
        for (const m of matchesWithContext) {
          allMatches.push({
            ...m,
            __subjectName: r.name,
            __subjectKey: r.subject?.key,
            __roles: r.roles,
            __subjectMeta: r?.subject?.meta || {},
          });
        }
      }

      if (runToken !== screeningRunTokenRef.current) return;
      const failed = perSubject.filter((x) => !x.ok && x.name);
      if (failed.length) {
        setScreeningErr(sanctionsScreeningFailureMessage(failed));
      }

      const aggregate = {
        ok: true,
        subjects: [...(target ? screeningResult?.subjects || [] : []), ...subjects],
        subjectsSig,
        contextSubjectsSig: subjectsSig,
        results: [...(target ? screeningResult?.results || [] : []), ...perSubject],
        errors: [...(target ? screeningResult?.errors || [] : []), ...failed.map(item => ({ name: item.name, key: item.subject?.key, code: item.code }))],
        matches: [...(target ? screeningResult?.matches || [] : []), ...allMatches],
      };

      if (runToken !== screeningRunTokenRef.current) return;
      const runAt = new Date().toISOString();
      autoClearRunRef.current = runAt;
      setScreeningResult(aggregate);

      persistScreeningState({
        result: aggregate,
        contextSubjectsSig: subjectsSig,
        personReviews: keptReviews,
        relevantKeys: keptSelections,
        runAt,
      });

    } catch (e) {
      if (runToken === screeningRunTokenRef.current) setScreeningErr("Sanctions screening is currently unavailable");
    } finally {
      if (runToken === screeningRunTokenRef.current) { setScreeningLoading(false); setRunningSubjectKey(null); }
    }
  }

  useEffect(() => {
    onScreeningStateChange?.(screeningLoading);
  }, [onScreeningStateChange, screeningLoading]);

  useImperativeHandle(
    ref,
    () => ({
      runScreening: runSanctionsScreening,
      hasRun: hasResult,
      isLoading: screeningLoading,
      canRun: !readOnly && !screeningLoading && !!screeningSubjects?.length,
    }),
    [hasResult, screeningLoading, readOnly, screeningSubjects, effectiveSanctionsSources]
  );

  function clearScreening() {
    autoClearRunRef.current = null;
    cancelDetailsCollapse();
    setPersonDecisions({});
    setRelevantKeys(new Set());
    setDecisionEditKeys(new Set());
    setScreeningErr("");
    setScreeningResult(null);
    setOpenKey(null);

    if (typeof writeAt === "function") {
      writeAt(["internalUse", "sanctionsScreening"], null);
    }
    resetComplianceAssessment();
  }

  // ---------------- NORMALIZE + DEDUPE MATCHES ----------------
  const uniqueMatches = useMemo(() => {
    const matches = Array.isArray(screeningResult?.matches)
      ? screeningResult.matches
      : [];
    if (!matches.length) return [];

    const map = new Map();

    for (const m of matches) {
      const name =
        clean(m?.record?.name) ||
        clean(m?.matchedName) ||
        clean(m?.name) ||
        clean(m?.candidateName) ||
        clean(m?.record?.name) ||
        "Unknown";

      const src =
        clean(
          m?.source || m?.key || m?.list || m?.record?.source || ""
        ).toUpperCase() || "LIST";

      const subjectName = clean(m?.__subjectName || m?.subjectName || (screeningSubjects.length === 1 ? screeningSubjects[0].name : ""));
      const subjectKey = clean(m?.__subjectKey || m?.subjectKey) || (subjectName ? normKey(subjectName) : "unknown_subject");

      const key = matchIdentityKey(m);

      const score = Number(m?.score);
      const strength = sanctionsNameStrength(m);

      const prev = map.get(key);
      if (!prev) {
        map.set(key, {
          _key: key,
          name,
          source: src,
          score: Number.isFinite(score) ? score : null,
          strength,
          matchType: clean(m?.matchType || m?.reason || m?.type || ""),
          record: m?.record || m,
          subject: screeningSubjects.find(subject => subject.key === subjectKey) || { key: subjectKey, name: subjectName, roles: m.__roles || [] },
          subjectName,
          roles: Array.isArray(m?.__roles) ? m.__roles : [],
          subjectMeta: m?.__subjectMeta || {},
          contextAssessment: m?.contextAssessment || defaultContextAssessment(),
          _raw: m,
        });
      } else {
        const prevScore = Number(prev.score);
        const nextScore = Number(score);
        const bestRaw = !Number.isFinite(prevScore)
          ? m
          : Number.isFinite(nextScore) && nextScore > prevScore
          ? m
          : prev._raw;

        const bestScore = Number(bestRaw?.score);
        prev._raw = bestRaw;
        prev.score = Number.isFinite(bestScore) ? bestScore : prev.score;
        prev.strength = sanctionsNameStrength(bestRaw);

        const mt = clean(
          bestRaw?.matchType || bestRaw?.reason || bestRaw?.type || ""
        );
        if (mt) prev.matchType = mt;

        const rec = bestRaw?.record || bestRaw;
        if (rec) prev.record = rec;
        prev.subjectMeta = bestRaw?.__subjectMeta || prev.subjectMeta || {};
        prev.contextAssessment =
          bestRaw?.contextAssessment ||
          prev.contextAssessment ||
          defaultContextAssessment();
      }
    }

    const out = Array.from(map.values());
    out.sort(compareSanctionsNameMatches);

    return out;
  }, [screeningResult, screeningSubjects]);

  const styles = useMemo(
    () =>
      toneStyles({
        hasResult: !!screeningResult,
        hasMatches: uniqueMatches.length > 0,
      }),
    [screeningResult, uniqueMatches.length]
  );

  // Keep every hit in its person group; filter the displayed evidence after
  // a person-level decision so editing the outcome can reveal all hits again.
  const visibleMatches = useMemo(
    () => (screeningResult ? uniqueMatches : []),
    [screeningResult, uniqueMatches]
  );

  const sanctionsReviewHeaderSticky = true;
  const sanctionsReviewHeaderGlass = sanctionsReviewHeaderSticky && sanctionsReviewScrolled;

  const groupedSanctionsReviewItems = useMemo(() => {
    const map = new Map();
    const subjectOrder = new Map();
    (allScreeningSubjects || []).forEach((subject, index) => {
      const key = clean(subject?.key) || `subject_${index}`;
      subjectOrder.set(key, index);
    });

    function ensureGroup({ key, name, roles, meta }) {
      const groupKey = clean(key) || `subject:${normKey(name || "subject")}`;
      if (!map.has(groupKey)) {
        map.set(groupKey, {
          key: groupKey,
          name: clean(name) || "Subject",
          roles: Array.isArray(roles) ? roles : [],
          meta: meta || {},
          matches: [],
          order: subjectOrder.has(groupKey)
            ? subjectOrder.get(groupKey)
            : Number.MAX_SAFE_INTEGER,
        });
      }
      const group = map.get(groupKey);
      if (!group.name && name) group.name = clean(name);
      if (!group.roles.length && Array.isArray(roles)) group.roles = roles;
      if (!Object.keys(group.meta || {}).length && meta) group.meta = meta;
      return group;
    }

    // A screened person still needs an outcome and a place for manual evidence
    // when the watchlist returns no hits.
    (allScreeningSubjects || []).forEach(ensureGroup);

    (visibleMatches || []).forEach((match) => {
      const subject = match?.subject || {};
      const group = ensureGroup({
        key: subject.key,
        name: subject.name,
        roles: subject.roles,
        meta: subject.meta,
      });
      group.matches.push(match);
    });

    const groups = Array.from(map.values());
    for (const group of groups) {
      group.matches = orderScreeningHits(group.matches, match => ({
        strength: match.strength,
        score: match.score,
        context: assessContext(match.subject?.meta || match.subjectMeta || {}, match.record, "sanctions"),
      }));
    }
    const visibleGroups = groups.filter(group => allScreeningSubjects.some(subject => normKey(subject.name) === normKey(group.name)));

    return visibleGroups.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.name.localeCompare(b.name);
    });
  }, [
    allScreeningSubjects,
    visibleMatches,
  ]);

  function subjectHasSelectedEvidence(key, selectedKeys = relevantKeys) {
    const selected = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);
    const group = groupedSanctionsReviewItems.find(item => item.key === key);
    return !!group && (
      group.matches.some(match => selected.has(match._key))
    );
  }

  function contextReviewChanged(key, decisions = personDecisions) {
    const saved = decisions[key]?.contextFingerprint;
    return !!saved && (saved !== contextFingerprint(screeningSubjects.find(s => s.key === key)?.meta) || decisions[key]?.contextRuleVersion !== CONTEXT_VERSION);
  }

  function isSubjectReviewed(key, decisions = personDecisions, selectedKeys = relevantKeys, notes = sanctions?.supportingInformation) {
    if (contextReviewChanged(key, decisions)) return false;
    if (screeningResult?.errors?.some(item => (item.key || normKey(item.name)) === key)) return false;
    return isSubjectReviewComplete(decisions[key]?.decision, subjectHasSelectedEvidence(key, selectedKeys), notes?.[key]);
  }

  const decidedCount = screeningSubjects.filter(subject => isSubjectReviewed(subject.key)).length;
  const isReviewCompleted = !blockedSubjects.length && !hasScreeningErrors && screeningSubjects.length > 0 && decidedCount === screeningSubjects.length;

  // Apply once per new screening run, so reopening or clearing an outcome
  // remains editable and is never immediately auto-selected again.
  useEffect(() => {
    if (readOnly || !autoClearRunRef.current || screeningLoading) return;
    if (form?.internalUse?.sanctionsScreening?.runAt !== autoClearRunRef.current) return;
    const keys = noMatchSubjectKeys("sanctions", screeningSubjects, screeningResult, undefined);
    if (keys === null) return;
    autoClearRunRef.current = null;
    const next = { ...personDecisions };
    let changed = false;
    for (const key of keys) {
      if (next[key]?.decision || decisionEditKeys.has(key) || subjectHasSelectedEvidence(key)) continue;
      next[key] = {
        decision: "no_exposure",
        contextFingerprint: contextFingerprint(screeningSubjects.find(subject => subject.key === key)?.meta),
        contextRuleVersion: CONTEXT_VERSION,
        decidedAt: new Date().toISOString(),
        decidedByUid: auth?.currentUser?.uid || "",
      };
      changed = true;
    }
    if (!changed) return;
    setPersonDecisions(next);
    persistScreeningState({ personReviews: next });
    updateReviewCompletion(next, relevantKeys, sanctions?.supportingInformation, true);
  });

  function renderDetails(rec) {
    const r = rec || {};
    const lines = [];

    const designation = Array.isArray(r?.designation)
      ? r.designation.filter(Boolean).join(", ")
      : clean(r?.designation);
    const nationality = Array.isArray(r?.nationality)
      ? r.nationality.filter(Boolean).join(", ")
      : clean(r?.nationality);
    const dob = Array.isArray(r?.dob)
      ? r.dob.filter(Boolean).join(", ")
      : clean(r?.dob);
    const pob = Array.isArray(r?.pob)
      ? r.pob.filter(Boolean).join(" · ")
      : clean(r?.pob);
    const addr = Array.isArray(r?.addresses)
      ? r.addresses.filter(Boolean).join(" · ")
      : clean(r?.address);
    const notes = clean(
      r?.remarks || r?.comments || r?.COMMENTS1 || r?.comments1
    );

    if (designation) lines.push({ k: "Designation", v: designation });
    if (nationality) lines.push({ k: "Nationality", v: nationality });
    if (dob) lines.push({ k: "Date of birth", v: dob });
    if (pob) lines.push({ k: "Place of birth", v: pob });
    if (addr) lines.push({ k: "Address", v: addr });
    if (notes) lines.push({ k: "Notes", v: notes });

    if (!lines.length)
      return (
        <div className="small text-muted">No additional details available.</div>
      );

    return (
      <div className="small">
        {lines.map((x, i) => (
          <div key={i} className="mb-1">
            <span className="text-muted">{x.k}:</span>{" "}
            <span style={{ color: "#2f3441" }}>{x.v}</span>
          </div>
        ))}
      </div>
    );
  }

  const staffDecision = clean(sanctions?.staffDecision) || "";
  function setStaffDecision(dec) {
    if (typeof writeAt !== "function") return;
    writeAt(["sanctions", "staffDecision"], dec);
  }

  const phase2 =
    staffDecision === "flagged_remediated"
      ? "flagged_remediated"
      : staffDecision === "flagged_confirmed"
      ? "flagged_confirmed"
      : "";

  function setPhase2(v) {
    if (!hasResult) return;
    if (!isReviewCompleted) return;
    cancelDetailsCollapse();
    if (v === "flagged_remediated" || v === "flagged_confirmed") {
      if (staffDecision === v) {
        setStaffDecision("flagged");
        return;
      }
      setStaffDecision(v);
      scrollSanctionsAnchorThenCollapse();
    }
  }

  // ---------------- CLIENT DECLARATION ----------------
  const clientDeclared = !!sanctions?.clientDeclared;

  const potentialCount = hasResult ? uniqueMatches.length : 0;
  const exposureCount = screeningSubjects.filter(
    (subject) => personDecisions[subject.key]?.decision === "exposure"
  ).length;

  const complianceUsable = hasResult && !screeningLoading && isReviewCompleted;
  const shouldHighlightComplianceAssessment =
    isReviewCompleted && exposureCount > 0 && !phase2;

  function renderSanctionsMatchRow(m) {
    // Selecting a hit controls which evidence stays visible after review, without
    // changing decisions, risk or completion. Historical confirmations remain in the saved
    // dossier but are no longer editable per hit.
    const relevant = relevantKeys.has(m._key);
    const expanded = openKey === m._key;
    const niceReason = sanctionsMatchReason(m._raw || m, m.name);
    const showReason = niceReason && niceReason !== "Potential match";
    const context = assessContext(m.subject?.meta || m.subjectMeta || {}, m.record, "sanctions");
    const parts = [];
    const rec = m.record || {};
    const summarize = value => Array.isArray(value) ? value.filter(Boolean).join(", ") : clean(value);
    if (summarize(rec.dob)) parts.push(`Birth: ${summarize(rec.dob)}`);
    if (summarize(rec.designation)) parts.push(`Designation: ${summarize(rec.designation)}`);
    if (summarize(rec.nationality)) parts.push(`Nationality: ${summarize(rec.nationality)}`);

    return (
      <div
        key={m._key}
        className="list-group-item"
        style={{
          borderLeft: relevant ? "4px solid #EBE9E8" : "1px solid #DEE2E6",
          background: relevant ? "#FFFDFC" : "#FBFBFB",
          color: "#837E7B",
        }}
      >

        <div
          className="d-flex align-items-center justify-content-between gap-3"
          style={{ cursor: "pointer" }}
          onClick={() => setOpenKey(expanded ? null : m._key)}
          role="button"
        >
          <div style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-semibold" style={{ color: "#2f3441" }}>
                {m.name}
              </div>

              {sourcePill(m.source)}
              {badge(overallMatchAssessment(m.strength, context).label, overallMatchAssessment(m.strength, context).scheme)}
              <ContextLimitation context={context} />

            </div>

            <div className="mt-1">
              {showReason ? (
                <div className="pepNameMatchNote mt-1">{niceReason}</div>
              ) : null}

              {parts.length ? (
                <div
                  className="small text-muted mt-1"
                  style={{
                    display: "-webkit-box",
                    opacity: 0.9,
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 6,
                  }}
                >
                  {parts.join(" · ")}
                </div>
              ) : null}
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
            <button
              type="button"
              className="badge btn-lightBlue-outline kycSanctionsHitSelect"
              style={{
                fontWeight: 400,
                cursor: readOnly ? "default" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 26,
                padding: "0 10px",
              }}
              disabled={readOnly}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleRelevant(m._key);
              }}
              title={relevant ? "Deselect this supporting hit" : "Select this supporting hit"}
              aria-pressed={relevant}
            >
              {relevant ? "Selected ✓" : "Select"}
            </button>

            {relevant ? (
              <i
                className="bi bi-bookmark-star-fill"
                style={{ color: "#798CA0", fontSize: 18 }}
              />
            ) : (
              <i className="bi bi-bookmark" style={{ color: "#798CA0", fontSize: 18 }} />
            )}


            <span className="text-muted" title="Show details">
              <i className={`bi ${expanded ? "bi-chevron-up" : "bi-chevron-down"}`} />
            </span>
          </div>
        </div>

        <IdentityContext context={context} strength={m.strength} nameReason={niceReason}
          screenedName={m.subject?.name || m.subjectName} comparedName={m.name} />

        <AnimatePresence initial={false}>
          {expanded ? (
            <motion.div key="details" {...detailsIn} style={{ overflow: "hidden" }}>
              <div className="mt-2 p-2 rounded-3" style={{ background: "#F6F4F2" }}>
                {renderDetails(m.record)}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="card kycCard mb-3" style={collapseReviewSnapshot ? { overflowAnchor: "none" } : undefined}>
      <div className="kycSectionHead position-relative">
        <h5 className="kycSectionTitle mb-0">{sectionNo} | Sanctions Exposure</h5>
      </div>

      <div className="kycSectionBody">
        {screeningErr ? (
          <div className={`alert ${screeningResult?.errors?.length && screeningResult.errors.every(item => item.code === "insufficient_name") ? "alert-light text-muted" : "alert-danger"} mb-3`}>{screeningErr}</div>
        ) : null}

        {/* ----------- TOP SUMMARY CARD ----------- */}

        {/* background color top card overview main */}
        <div
          className="p-3 border rounded-3 mb-3"
          // style={{ background: "#C88294" }}
          style={{ background: "#EBECEF", cursor: readOnly ? "default" : "pointer" }}
          role={readOnly ? undefined : "button"}
          tabIndex={readOnly ? undefined : 0}
          onClick={readOnly ? undefined : toggleDetails}
          onKeyDown={
            readOnly
              ? undefined
              : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleDetails();
                  }
                }
          }
        >
          {(() => {
            const clientStatusBadge = clientDeclared
              ? badge("Declared", "warning")
              : badge("Not declared", "success");
            const clientHint = clientDeclared
              ? "Client reports exposure"
              : "Client reports no exposure";

            const screeningStatusBadge = screeningLoading
              ? badge("Running", "warning")
              : !hasResult
              ? badge("Pending", "grey")
              : hasScreeningErrors
              ? badge("Pending", "grey")
              : potentialCount === 0 && !blockedSubjects.length
              ? badge("No matches found", "success")
              : isReviewCompleted
              ? badge(
                  exposureCount > 0
                    ? `${exposureCount} Sanctions exposure`
                    : "Reviewed · No exposure",
                  exposureCount > 0 ? "danger" : "success"
                )
              : badge("Pending", "grey");

            const screeningHint = screeningLoading
              ? "Screening in progress"
              : !hasResult
              ? "Screening not started"
              : isReviewCompleted
              ? screeningSubjects.length === 1
                ? "Subject reviewed"
                : `All ${screeningSubjects.length} subjects reviewed`
              : `${decidedCount} of ${allScreeningSubjects.length} ${pluralize(allScreeningSubjects.length, "subject", "subjects")} reviewed`;

            const complianceStatusBadge = !complianceUsable
              ? badge("Pending", "grey")
              : exposureCount === 0
              ? badge("Cleared", "success")
              : staffDecision === "flagged_remediated"
              ? badge("Cleared", "success")
              : staffDecision === "flagged_confirmed"
              ? badge("Confirmed", "danger")
              : badge("Sanctions exposure identified", "warning");

            const complianceHint = !hasResult
              ? "Set outcome after screening"
              : !complianceUsable
              ? "Set outcome after review"
              : exposureCount === 0
              ? "No sanctions exposure identified"
              : staffDecision === "flagged_remediated"
              ? "Exposure flagged · Remediated"
              : staffDecision === "flagged_confirmed"
              ? "Confirmed sanctions exposure"
              : "Sanctions exposure identified · Pending decision";

            const overall = (() => {
              if (!complianceUsable) {
                return {
                  term: "Pending",
                  bg: "#F4F5F7",
                  border: "#D9DCE1",
                  icon: "bi-hourglass",
                  color: "#6B7C5F",
                  lottie: overallPendingAnim,
                };
              }
              if (exposureCount === 0) {
                return {
                  term: "Cleared",
                  bg: "#EAF7EE",
                  border: "#BFE6CC",
                  icon: "bi-check-circle",
                  color: "#6B7C5F",
                  lottie: overallClearedAnim,
                };
              }
              if (staffDecision === "flagged_confirmed") {
                return {
                  term: "Confirmed",
                  bg: "#FDEBEC",
                  border: "#F3B6BC",
                  icon: "bi-exclamation-circle-fill",
                  color: "#C88294",
                  lottie: overallAlertAnim,
                };
              }
              if (staffDecision === "flagged_remediated") {
                return {
                  term: "Cleared",
                  bg: "#EAF7EE",
                  border: "#BFE6CC",
                  icon: "bi-check-circle",
                  color: "#6B7C5F",
                  lottie: overallClearedAnim,
                };
              }
              return {
                term: "Pending",
                bg: "#F4F5F7",
                border: "#FFD79A",
                icon: "bi-hourglass",
                color: "#6B7C5F",
                lottie: overallPendingAnim,
              };
            })();

            return (
              <>
                <div
                  className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                  style={{ height: 30, marginBottom: 5, marginTop: 0 }}
                >
                  <div>
                    <div
                      className="fw-semibold d-flex align-items-center gap-2"
                      style={{ color: "#2f3441" }}
                    >
                      {/* <i
                        className="bi bi-bank2 me-2"
                        style={{ color: "#A1A5AE", fontSize: 25 }}
                      /> */}
                      <span
                        style={{
                          width: 40,
                          height: 40,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "0 0 auto",
                        }}
                      >
                        <Lottie
                          animationData={pepAnim}
                          loop
                          autoplay
                          style={{ width: 40, height: 40 }}
                        />
                      </span>
                      Sanctions overview
                    </div>
                  </div>

                  <div
                    className="d-flex align-items-center gap-2"
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {(hasResult || screeningLoading) && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                        style={{ height: 30, width: 30 }}
                        aria-label={screeningLoading ? "Screening in progress" : "Re-run screening"}
                        disabled={readOnly || screeningLoading || !screeningSubjects.length}
                        onClick={(e) => {
                          e.stopPropagation();
                          runSanctionsScreening();
                        }}
                      >
                        <i className="bi bi-arrow-repeat" aria-hidden="true" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDetails();
                      }}
                      disabled={readOnly}
                      style={{ height: 30 }}
                    >
                      {showDetails ? (
                        <>
                          <i className="bi bi-chevron-up me-1" />
                          Hide details
                        </>
                      ) : (
                        <>
                          <i className="bi bi-chevron-down me-1" />
                          Show details
                        </>
                      )}
                    </button>
                  </div>
                </div>
                {/* <div
                  className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                  style={{ height: 30, marginBottom: 5, marginTop: 0 }}
                >
                  <div>
                    <div
                      className="fw-semibold d-flex align-items-center gap-2"
                      style={{ color: "#2f3441" }}
                    >
                      <i
                        className="bi bi-bank2 me-2"
                        style={{ color: "#A1A5AE", fontSize: 25 }}
                      />
                      Sanctions overview
                    </div>
                  </div>
                </div> */}

                <hr className="my-3" />

                <div className="row g-2 pepTilesGrid">
                  <div className="col-12 col-md">
                    <div className="p-2 rounded-3 border bg-white h-100">
                      <div
                        className="d-flex align-items-center justify-content-between gap-2"
                        style={{ marginTop: 3 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* <i className="bi bi-person-check" /> */}
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 22,
                              height: 22,
                              color: "#817672",
                              flex: "0 0 auto",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              id="fi_8703172"
                              height="512"
                              viewBox="0 0 60 60"
                              width="512"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fill="currentColor"
                                d="m34.82 8.06c-5.71 0-10.35 4.64-10.35 10.35s4.64 10.35 10.35 10.35 10.35-4.64 10.35-10.35-4.65-10.35-10.35-10.35zm0 18.69c-4.6 0-8.35-3.74-8.35-8.35s3.74-8.35 8.35-8.35c4.6 0 8.35 3.74 8.35 8.35s-3.75 8.35-8.35 8.35z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="m22.54 38.64c.66.61 1.04 1.49 1.04 2.41v9.3c0 .55.45 1 1 1s1-.45 1-1v-9.3c0-1.47-.61-2.88-1.68-3.88-5.42-5.04-10.5-12.12-15.54-21.67-.73-1.39-.32-3.14.95-4.07.75-.54 1.7-.73 2.6-.52s1.66.8 2.09 1.61c3.05 5.79 6.49 11.08 10.21 15.73 1.83 2.28 4.56 3.58 7.49 3.58h9.16c6.14 0 11.14 5 11.14 11.14v7.37c0 .55.45 1 1 1s1-.45 1-1v-7.37c0-7.25-5.89-13.14-13.14-13.14h-9.16c-2.33 0-4.49-1.03-5.93-2.83-3.64-4.54-7.01-9.73-10-15.41-.7-1.33-1.94-2.29-3.4-2.63s-3-.03-4.23.85c-2.08 1.51-2.74 4.35-1.55 6.62 5.15 9.76 10.36 17.02 15.95 22.21z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="m46 43.71v7.23c0 .55.45 1 1 1s1-.45 1-1v-7.23c0-.55-.45-1-1-1s-1 .44-1 1z"
                              ></path>
                            </svg>
                          </span>
                          <div className="fw-semibold small">
                            Client declaration
                          </div>
                        </div>
                        {clientStatusBadge}
                      </div>
                      <div className="small text-muted mt-1">{clientHint}</div>
                    </div>
                  </div>

                  <div className="col-12 col-md">
                    <div className="p-2 rounded-3 border bg-white h-100">
                      <div
                        className="d-flex align-items-center justify-content-between gap-2"
                        style={{ marginTop: 3 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* <i className="bi bi-search" /> */}
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 22,
                              height: 22,
                              color: "#817672",
                              flex: "0 0 auto",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              height="512"
                              viewBox="0 0 64 64"
                              width="512"
                              xmlns="http://www.w3.org/2000/svg"
                              id="fi_6772342"
                            >
                              <g
                                id="_10-Security_Gate"
                                data-name="10-Security Gate"
                              >
                                <path
                                  fill="currentColor"
                                  d="m53 2h-42a.99974.99974 0 0 0 -1 1v58a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-43h32v43a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-58a.99974.99974 0 0 0 -1-1zm-39 58h-2v-56h2zm34-44h-32v-12h32zm4 44h-2v-56h2zm-26-46h12a.99974.99974 0 0 0 1-1v-6a.99974.99974 0 0 0 -1-1h-12a.99974.99974 0 0 0 -1 1v6a.99974.99974 0 0 0 1 1zm1-6h10v4h-10zm-9 0a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm23-4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm-4 20h-.54047a6 6 0 1 0 -8.91906 0h-.54047a7.00818 7.00818 0 0 0 -7 7v6a3.00328 3.00328 0 0 0 3 3h2v11a3.00328 3.00328 0 0 0 3 3h8a3.00328 3.00328 0 0 0 3-3v-11h2a3.00328 3.00328 0 0 0 3-3v-6a7.00818 7.00818 0 0 0 -7-7zm-5-8a4 4 0 1 1 -4 4 4.00427 4.00427 0 0 1 4-4zm10 21a1.001 1.001 0 0 1 -1 1h-2v-5a1 1 0 0 0 -2 0v18a1.001 1.001 0 0 1 -1 1h-3v-9a1 1 0 0 0 -2 0v9h-3a1.001 1.001 0 0 1 -1-1v-18a1 1 0 0 0 -2 0v5h-2a1.001 1.001 0 0 1 -1-1v-6a5.00588 5.00588 0 0 1 5-5h10a5.00588 5.00588 0 0 1 5 5zm-34-13a.99974.99974 0 0 1 -1 1h-4a1 1 0 0 1 0-2h4a.99974.99974 0 0 1 1 1zm-4.55469-9.168a1 1 0 1 1 1.10938-1.664l3 2a1 1 0 1 1 -1.10938 1.664zm4.38669 16.61331a.9993.9993 0 0 1 -.27731 1.38669l-3 2a1 1 0 0 1 -1.10938-1.664l3-2a.99886.99886 0 0 1 1.38669.27731zm54.168-7.44531a.99974.99974 0 0 1 -1 1h-4a1 1 0 0 1 0-2h4a.99974.99974 0 0 1 1 1zm-5.832-7.44531a.9993.9993 0 0 1 .27734-1.38672l3-2a1 1 0 1 1 1.10935 1.66403l-3 2a.99992.99992 0 0 1 -1.38672-.27734zm4.38669 16.61331a1 1 0 1 1 -1.10938 1.664l-3-2a1 1 0 0 1 1.10938-1.664z"
                                ></path>
                              </g>
                            </svg>
                          </span>
                          <div className="fw-semibold small">Screening</div>
                        </div>
                        {screeningStatusBadge}
                      </div>
                      <div className="small text-muted mt-1">
                        {screeningHint}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md">
                    <div className="p-2 rounded-3 border bg-white h-100">
                      <div
                        className="d-flex align-items-center justify-content-between gap-2 flex-wrap"
                        style={{ marginTop: 3 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* <i className="bi bi-clipboard-check" /> */}
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 22,
                              height: 22,
                              color: "#817672",
                              flex: "0 0 auto",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              id="fi_14979987"
                              viewBox="0 0 60 60"
                            >
                              <path
                                fill="currentColor"
                                d="M36.949,31.846A3.005,3.005,0,0,0,39,29V26a9,9,0,0,0-18,0v3a3.005,3.005,0,0,0,2.051,2.846A7,7,0,0,0,26,36.734v1.5l-3.9,1.053a7.242,7.242,0,0,0-5.228,4.542L15.6,47.4l-2.4-1.917a1.579,1.579,0,0,0-.2-.13V42.68a2.685,2.685,0,0,0-5.29-.642l-.007.026a4.629,4.629,0,0,0-4.211,1.453,1.892,1.892,0,0,0,.617,2.995,19.054,19.054,0,0,1,3.045,1.7c.465.336.843.633,1.214.935a1.83,1.83,0,0,0,.651,1.2l6.43,4.77a3.45,3.45,0,0,0,3.086.8,3.406,3.406,0,0,0,2.415-1.958L22,51.992V58a2,2,0,0,0,2,2H36a2,2,0,0,0,2-2V51.987l1.013,1.892a3.456,3.456,0,0,0,2.452,2.036,3.4,3.4,0,0,0,3.016-.744l6.568-4.882a1.769,1.769,0,0,0,.583-1.14c.371-.3.747-.6,1.207-.933a19.142,19.142,0,0,1,3.055-1.706,1.891,1.891,0,0,0,.6-3,4.641,4.641,0,0,0-4.2-1.442l-.007-.029A2.685,2.685,0,0,0,47,42.68v2.668a1.718,1.718,0,0,0-.2.131l-2.326,1.937-1.345-3.588a7.219,7.219,0,0,0-5.184-4.53L34,38.234v-1.5A7,7,0,0,0,36.949,31.846ZM30,19a7.009,7.009,0,0,1,7,7v.179c-.037-.013-.072-.032-.11-.044a3.073,3.073,0,0,0-4.626-1.743,4.521,4.521,0,0,1-4.527,0,3.075,3.075,0,0,0-4.629,1.743c-.037.011-.071.03-.108.043V26A7.009,7.009,0,0,1,30,19ZM24,30a1,1,0,0,1,0-2,1.081,1.081,0,0,0,1-1.075,1.031,1.031,0,0,1,.564-.9,1.1,1.1,0,0,1,.51-.129,1.023,1.023,0,0,1,.564.169,6.5,6.5,0,0,0,6.723,0,1.041,1.041,0,0,1,1.073-.04A1.064,1.064,0,0,1,35,27a1,1,0,0,0,1,1,1,1,0,0,1,0,2,1,1,0,0,0-1,1,5,5,0,0,1-10,0A1,1,0,0,0,24,30Zm9.439,10.155,1.921.517a6.768,6.768,0,0,1-2.036,2.614.392.392,0,0,1-.365.049.38.38,0,0,1-.251-.261,5.694,5.694,0,0,0-.793-1.654A4.831,4.831,0,0,0,33.439,40.155ZM31.984,52.646,30,53.965l-1.984-1.319L28.862,47h2.276ZM28.721,45l-.116-.348a2.294,2.294,0,0,0,.6-1.006A3.239,3.239,0,0,1,30,42.215a3.2,3.2,0,0,1,.788,1.417,2.323,2.323,0,0,0,.608,1.019L31.279,45Zm-1.433-1.911a.373.373,0,0,1-.247.246.4.4,0,0,1-.37-.052,6.8,6.8,0,0,1-2.034-2.609l1.924-.519a4.851,4.851,0,0,0,1.525,1.266A5.738,5.738,0,0,0,27.288,43.089ZM5.075,44.754a2.634,2.634,0,0,1,2.935-.522,1,1,0,0,0,1.38-.669l.259-1.037A.7.7,0,0,1,10.32,42a.681.681,0,0,1,.68.68v2.769a1.8,1.8,0,0,0-.233.205l-1.524,1.63c-.28-.221-.581-.451-.924-.7A20.843,20.843,0,0,0,5.075,44.754ZM49.68,42a.7.7,0,0,1,.671.522l.259,1.041a1,1,0,0,0,1.38.669,2.69,2.69,0,0,1,2.962.509,20.887,20.887,0,0,0-3.278,1.849c-.339.245-.637.473-.916.693l-1.525-1.629A1.8,1.8,0,0,0,49,45.449V42.68A.681.681,0,0,1,49.68,42Zm-8.424,2.53L42.6,48.118a1.956,1.956,0,0,0,3.052.84l2.248-1.8,1.647,1.759-6.327,4.7a1.478,1.478,0,0,1-1.321.342,1.542,1.542,0,0,1-1.087-.949l-2.931-5.486A1,1,0,0,0,36,48V58H24V48a1,1,0,0,0-1.882-.472l-2.969,5.564a1.481,1.481,0,0,1-1.049.871,1.532,1.532,0,0,1-1.389-.4L10.45,48.921,12.1,47.159l2.248,1.8a1.955,1.955,0,0,0,3.052-.84l1.345-3.586a5.271,5.271,0,0,1,3.832-3.3l.1-.027a9.027,9.027,0,0,0,2.805,3.692,2.356,2.356,0,0,0,1.241.443l.25.75-.956,6.388a1.828,1.828,0,0,0,.794,1.774L28.989,55.7a1.823,1.823,0,0,0,2.022,0l2.176-1.446a1.831,1.831,0,0,0,.793-1.781l-.955-6.381.247-.742a2.359,2.359,0,0,0,1.239-.449,8.969,8.969,0,0,0,2.806-3.7l.151.04A5.247,5.247,0,0,1,41.256,44.53ZM32,38.743a2.944,2.944,0,0,1-2,1.243,2.946,2.946,0,0,1-2-1.241V37.706a6.95,6.95,0,0,0,4,0Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M11,22A11,11,0,1,0,0,11,11.013,11.013,0,0,0,11,22ZM11,2a9,9,0,1,1-9,9A9.01,9.01,0,0,1,11,2Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M49,0A11,11,0,1,0,60,11,11.013,11.013,0,0,0,49,0Zm0,20a9,9,0,1,1,9-9A9.01,9.01,0,0,1,49,20Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M7.293,14.707a1,1,0,0,0,1.414,0L11,12.414l2.293,2.293a1,1,0,0,0,1.414-1.414L12.414,11l2.293-2.293a1,1,0,1,0-1.414-1.414L11,9.586,8.707,7.293A1,1,0,1,0,7.293,8.707L9.586,11,7.293,13.293A1,1,0,0,0,7.293,14.707Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M51.168,7.445l-3.323,4.986-1.138-1.138a1,1,0,0,0-1.414,1.414l2,2A1,1,0,0,0,48,15c.032,0,.065,0,.1-.005a1,1,0,0,0,.733-.44l4-6a1,1,0,0,0-1.664-1.11Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M50,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M12,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                              ></path>
                            </svg>
                          </span>
                          <div className="fw-semibold small">Assessment</div>
                        </div>
                        {complianceStatusBadge}
                      </div>
                      <div className="small text-muted mt-1">
                        {complianceHint}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-2">
                    <div
                      className="p-2 rounded-3 border h-100 d-flex align-items-center justify-content-center text-center"
                      style={{
                        background: overall.bg,
                        borderColor: overall.border,
                      }}
                    >
                      <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                        <span
                          style={{
                            width: 35,
                            height: 35,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            lineHeight: 1,
                          }}
                          aria-hidden="true"
                        >
                          <Lottie
                            animationData={overall.lottie}
                            loop
                            autoplay
                            style={{ width: 35, height: 35 }}
                          />
                        </span>
                        {/* <i
                          className={`bi ${overall.icon}`}
                          style={{
                            fontSize: 24,
                            lineHeight: 1,
                            color: overall.color,
                          }}
                          aria-hidden="true"
                        /> */}
                        <div
                          className="fw-semibold small"
                          style={{ color: overall.color, fontSize: 12 }}
                        >
                          {overall.term}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Extensions: card — arrow — card, rendered inside the overview band.
              Overflow is released after the height animation so the arrow can
              escape the band and land in the screening card below. */}
          <AnimatePresence initial={false} onExitComplete={() => finishCollapseScroll("extensions")}>
            {showDetails ? (
              <motion.div
                key="sanctions_ext_row"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={fullDetailsExit}
                transition={T.med}
                onAnimationStart={() => setExtRevealed(false)}
                onAnimationComplete={() => setExtRevealed(!!showDetails)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                style={{
                  cursor: "default",
                  overflow: extRevealed ? "visible" : "hidden",
                  position: "relative",
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div className="row g-2 pepTilesGrid" style={{ paddingTop: 8 }}>
                {/* 1) Client declaration — same idea as the middle: a short white
                    3D arrow drops from the tile above INTO this card (card itself flat) */}
                <div className="col-12 col-md d-flex">
                  <div className="w-100 position-relative" style={{ paddingTop: 0 }}>

                  <div
                    className="border rounded-3 h-100 d-flex flex-column w-100"
                    style={{ padding: "16px", background: "#F9F9F9" }}
                  >
                    <div className="mt-0 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                      <div
                        className="text-muted small"
                        style={{ fontWeight: 500 }}
                      >
                        Sanctions declared by client?
                      </div>

                      <div
                        className="btn-group"
                        role="group"
                        aria-label="Client declaration"
                      >
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            clientDeclared
                              ? "btn-toggle-off-lightgrey"
                              : "btn-toggle-on-lightgrey"
                          }`}
                          disabled={readOnly}
                          onClick={() =>
                            writeAt?.(["sanctions", "clientDeclared"], false)
                          }
                        >
                          No
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            clientDeclared
                              ? "btn-toggle-on-lightgrey"
                              : "btn-toggle-off-lightgrey"
                          }`}
                          disabled={readOnly}
                          onClick={() =>
                            writeAt?.(["sanctions", "clientDeclared"], true)
                          }
                        >
                          Yes
                        </button>
                      </div>

                    </div>

                    <div
                      className="mt-3"
                      style={{
                        background: "#FCFCFC",
                        border: "1px solid #EFECE8",
                        borderRadius: 8,
                        padding: 12,
                      }}
                    >
                      <div
                        className="text-muted small"
                        style={{ marginTop: 0, marginBottom: 8 }}
                      >
                        Client comments
                      </div>
                      <textarea
                        ref={clientCommentsRef}
                        className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                          "sanctions",
                          "clientDetails",
                        ])}`}
                        rows={1}
                        disabled={readOnly}
                        value={sanctions?.clientDetails || ""}
                        onChange={(e) => {
                          const el = e.currentTarget;
                          writeAt?.(["sanctions", "clientDetails"], e.target.value);
                          window.requestAnimationFrame(() =>
                            autoResizeTextarea(el)
                          );
                        }}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        placeholder="No comments"
                      />
                    </div>
                  </div>
                  </div>
                </div>

                {/* 2) Screening connector — a wide arrow pointing into the screening card below */}
                <div className="col-12 col-md d-flex">
                  {/* Relative shell keeps the layout box; the arrow itself extends
                      28px past it so the tip enters the screening card below. */}
                  <div className="w-100 position-relative">
                  {/* drop-shadow lives on this un-clipped wrapper so it traces the
                      arrow silhouette and shows the tip floating inside the card below */}
                  <motion.div
                    animate={
                      !showDetails && !collapseReviewSnapshot
                        ? // hide: ride up through the top fade-mask with the collapse
                          { opacity: 1, y: -160 }
                        : extRevealed
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: -8 }
                    }
                    transition={T.med}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: -100,
                      zIndex: 2,
                      filter: "drop-shadow(0 2px 3px rgba(76, 84, 101, 0.10))",
                      WebkitMaskImage:
                        "linear-gradient(to bottom, transparent 0px, #000 50px)",
                      maskImage:
                        "linear-gradient(to bottom, transparent 0px, #000 50px)",
                    }}
                  >
                  <div
                    className="d-flex flex-column"
                    style={{ height: "calc(100% - 14px)", width: "90%", margin: "0 auto" }}
                  >
                    <div
                      className="d-flex flex-column align-items-center text-center flex-grow-1 position-relative"
                      style={{
                        width: "100%",
                        marginBottom: -2,
                        position: "relative",
                        zIndex: 1,
                        background: "#EDEAE9",
                        border: "0.5px solid #DCD3C7",
                        borderBottom: "none",
                        borderRadius: 0,
                        padding: "16px 12px 8px",
                      }}
                    >
                      <div
                        className="flex-grow-1 d-flex flex-column align-items-center justify-content-center"
                        style={{ gap: 10 }}
                      >
                        {screeningLoading ? (
                          <>
                            <div
                              className="appDotsLoader"
                              aria-hidden="true"
                              style={{ transform: "scale(0.6)", transformOrigin: "center" }}
                            />
                            <div style={{ color: "#A5ABB5", fontSize: 12, fontWeight: 500 }}>
                              Screening...
                            </div>
                          </>
                        ) : !hasResult ? (
                          <>
                            <div className="small text-muted">
                              {screeningSubjects?.length ? (
                                <>
                                  Ready to screen {screeningSubjects.length} name
                                  {screeningSubjects.length > 1 ? "s" : ""}
                                </>
                              ) : (
                                "Missing name(s)"
                              )}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary kycPepSearchBtn"
                              style={{
                                width: 150,
                                minHeight: 38,
                                padding: "10px 18px",
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 3,
                              }}
                              onClick={runSanctionsScreening}
                              disabled={readOnly || !screeningSubjects?.length}
                              title={customerName ? "Run screening" : "Missing name"}
                            >
                              <img
                                src={doubleTapFullIcon}
                                alt=""
                                aria-hidden="true"
                                style={{ width: 32, height: 32, flex: "0 0 auto" }}
                              />
                              Search Sanctions
                            </button>
                          </>
                        ) : (
                          <div style={{ paddingTop: 34 }}>
                              {screeningSubjects?.length ? (
                                <div
                                  className="small text-muted mt-2"
                                  style={{ opacity: 0.9 }}
                                >
                                  Screening results
                                  <div style={{ fontSize: 11, color: "#9298A1", marginTop: 2 }}>
                                    {screeningSubjects.length}{" "}
                                    {pluralize(screeningSubjects.length, "subject", "subjects")}
                                    {" · "}
                                    {screenedRolesText(screeningSubjects)}
                                  </div>
                                </div>
                              ) : null}

                              {isReviewCompleted ? (
                                <div style={{ fontSize: 22, color: "#B7B9BF", marginTop: 8 }}>
                                  <span role="img" aria-label="Review completed">✓</span>
                                </div>
                              ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                    <svg
                          width="100%"
                          height="60"
                          viewBox="0 0 400 30"
                          preserveAspectRatio="none"
                          style={{ display: "block", flex: "0 0 auto" }}
                          aria-hidden="true"
                        >
                          <path
                            d="M0 0 H400 V3 Q400 12 376 15.8 L200 30 L24 15.8 Q0 12 0 3 Z"
                            fill="#EDEAE9"
                          />
                          {/* outline drawn separately so the head's TOP edge
                              (the shaft junction) carries no stroke at all */}
                          <path
                            d="M400 0 V3 Q400 12 376 15.8 L200 30 L24 15.8 Q0 12 0 3 V0"
                            fill="none"
                            stroke="#DCD3C7"
                            strokeWidth={0.5}
                            vectorEffect="non-scaling-stroke"
                          />
                        </svg>
                  </div>
                  </motion.div>
                  </div>
                </div>

                {/* 3) Compliance assessment — same white 3D arrow dropping from
                    the Assessment tile into the card; card starts below the arrow.
                    flexBasis adds the Overall tile's 2/12 so the card also spans
                    below the Confirmed tile. */}
                <div className="col-12 col-md d-flex pepExtAssessCol">
                  <div className="w-100 position-relative" style={{ paddingTop: 0 }}>
                  <div
                    className={`kycPepComplianceAssessmentCard border rounded-3 h-100 d-flex flex-column w-100 position-relative ${
                      complianceAssessmentGlowActive
                        ? "kycComplianceAssessmentGlow"
                        : ""
                    }`}
                    onAnimationEnd={(e) => {
                      if (e.target !== e.currentTarget) return;
                      setComplianceAssessmentGlowActive(false);
                      setComplianceAssessmentIntroBackgroundActive(false);
                    }}
                    style={
                      !complianceUsable || decisionEditKeys.size > 0 || (exposureCount === 0 && !SHOW_NO_EXPOSURE_CARD)
                        ? { padding: "16px", visibility: "hidden", pointerEvents: "none" }
                        : shouldHighlightComplianceAssessment
                        ? {
                            padding: "16px",
                            background:
                              complianceAssessmentIntroBackgroundActive
                                ? "#FFFEF8"
                                : "#F9F9F9",
                            borderColor: "#DCD0A0",
                            boxShadow:
                              "inset 0 0 0 1px rgba(220, 208, 160, 0.18), 0 0 10px rgba(126, 111, 60, 0.06)",
                            transition:
                              "background-color 0.65s ease, box-shadow 0.25s ease",
                          }
                        : { padding: "16px", background: "#F9F9F9" }
                    }
                  >
                    {exposureCount === 0 ? (
                      <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                        <span
                          className="badge badgeStandard"
                          style={{ background: "transparent", color: "#A5ABB5", border: "1px solid #D9DCE1" }}
                        >
                          No exposure flagged
                        </span>
                      </div>
                    ) : (
                    <>
                    {shouldHighlightComplianceAssessment &&
                      actionRequiredBadgeVisible && (
                      <div className="kycPepActionRequiredOverlay">
                      <span
                        className="d-inline-flex flex-column align-items-center justify-content-center kycActionRequiredBadge"
                        title="Action required"
                        aria-label="Action required"
                        style={{
                          width: 70,
                          height: 70,
                          border: "1px solid #DCD0A0",
                          borderRadius: "50%",
                          padding: "7px 6px 8px",
                          gap: 2,
                          fontSize: 8,
                          fontWeight: 500,
                          lineHeight: 1,
                          color: "#6C757D",
                          cursor: "default",
                          textAlign: "center",
                          userSelect: "none",
                          zIndex: 1,
                        }}
                      >
                        <img
                          src={handFingerUpIcon}
                          alt=""
                          aria-hidden="true"
                          style={{
                            width: 22,
                            height: 22,
                            display: "block",
                            flex: "0 0 auto",
                          }}
                        />
                        <span
                          style={{
                            display: "block",
                            width: "100%",
                            marginTop: 2,
                          }}
                        >
                          Action required
                        </span>
                      </span>
                      </div>
                    )}
                    <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                      {complianceUsable ? (
                        <div className="w-100 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                          <div
                            className="small text-muted"
                            style={{ fontWeight: 500 }}
                          >
                            {"Final assessment of identified exposure :"}
                          </div>
                          <div className="btn-group flex-wrap" role="group">
                                <button
                                  type="button"
                                  className={`btn btn-sm ${
                                    phase2 === "flagged_remediated"
                                      ? "btn-toggle-on-lightgrey"
                                      : "btn-toggle-off-lightgrey"
                                  } ${cc(["sanctions", "staffDecision"])}`}
                                  disabled={readOnly}
                                  onClick={() =>
                                    setPhase2("flagged_remediated")
                                  }
                                >
                                  Remediated
                                </button>

                                <button
                                  type="button"
                                  className={`btn btn-sm ${
                                    phase2 === "flagged_confirmed"
                                      ? "btn-toggle-on-lightgrey"
                                      : "btn-toggle-off-lightgrey"
                                  } ${cc(["sanctions", "staffDecision"])}`}
                                  disabled={readOnly}
                                  onClick={() => setPhase2("flagged_confirmed")}
                                >
                                  Confirmed
                                </button>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div
                      style={
                        !complianceUsable
                          ? { visibility: "hidden", pointerEvents: "none" }
                          : undefined
                      }
                      className="flex-grow-1 d-flex flex-column"
                    >

                      <div
                        className="mt-3"
                        style={{
                          background: "#FCFCFC",
                          border: "1px solid #EFECE8",
                          borderRadius: 8,
                          padding: 12,
                        }}
                      >
                        <div
                          className="text-muted small"
                          style={{ marginTop: 0, marginBottom: 8 }}
                        >
                          Compliance comments
                        </div>
                        <textarea
                          ref={complianceCommentsRef}
                          className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                            "sanctions",
                            "staffAssessment",
                          ])}`}
                          rows={1}
                          disabled={readOnly}
                          value={sanctions?.staffAssessment || ""}
                          onChange={(e) => {
                            const el = e.currentTarget;
                            writeAt?.(
                              ["sanctions", "staffAssessment"],
                              e.target.value
                            );
                            window.requestAnimationFrame(() =>
                              autoResizeTextarea(el)
                            );
                          }}
                          onInput={(e) => autoResizeTextarea(e.currentTarget)}
                          placeholder="No comments"
                        />
                      </div>
                    </div>

                    </>
                    )}

                  </div>
                  </div>
                </div>

                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        {/* Screening results card — outside the band; the arrow's tip lands inside it */}
                {/* 4) Screening results card (✅ animated entrance + layout) */}
                <AnimatePresence initial={false} mode="wait" onExitComplete={() => finishCollapseScroll("results")}>
                  {showDetails && screeningLoading && !runningSubjectKey ? (
                    <motion.div
                      key="sanctions-screening-placeholder"
                      className="p-3 border rounded-3 mb-3"
                      role="status"
                      aria-label="Preparing screening results"
                      aria-busy="true"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      style={{ marginTop: 8, position: "relative", zIndex: 0, isolation: "isolate" }}
                    >
                      <div
                        className="rounded-3 border p-3 placeholder-glow"
                        aria-hidden="true"
                        style={{ background: "#F9F7F5", color: "#D9DDE2" }}
                      >
                        <span className="placeholder d-block rounded mb-2" style={{ width: "25%", height: 14 }} />
                        <span className="placeholder d-block rounded mb-3" style={{ width: "12%", height: 10 }} />
                        <div className="rounded-3 border p-3" style={{ background: "#FCFBFA" }}>
                          <span className="placeholder d-block rounded mb-3" style={{ width: "35%", height: 12 }} />
                          <span className="placeholder d-block rounded mb-2" style={{ width: "85%", height: 10 }} />
                          <span className="placeholder d-block rounded" style={{ width: "65%", height: 10 }} />
                        </div>
                      </div>
                    </motion.div>
                  ) : showDetails && allScreeningSubjects.length > 0 ? (
                    <motion.div
                      key="sanctions-screening-card"
                      // Keep sticky subject headers below the overview connector arrow (z-index 2).
                      style={{ marginTop: 8, position: "relative", zIndex: 0, isolation: "isolate" }}
                      {...cardIn}
                      animate={{ ...cardIn.animate, height: "auto" }}
                      exit={fullDetailsExit}
                      layout="position"
                    >
                      <div
                        className="p-3 border rounded-3 mb-3"
                        // F5F3F2 FCFCFF
                      >
                        {hasResult && !hideScreeningSummary && <div
                          className="p-3 rounded-3 border"
                          style={{
                            background: styles.bg,
                            borderColor: styles.border,
                          }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                            <div>
                              {Array.isArray(screeningResult?.errors) &&
                              screeningResult.errors.length ? (
                                <div
                                  className="small text-muted mt-1"
                                  style={{ color: "#a36b6b" }}
                                >
                                  {sanctionsScreeningFailureMessage(screeningResult.errors)}
                                </div>
                              ) : null}
                            </div>

                            <div className="d-flex flex-column align-items-end gap-2">
                              <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={runSanctionsScreening}
                                  disabled={readOnly || screeningLoading}
                                  title="Re-run screening"
                                >
                                  <i className="bi bi-arrow-repeat" />
                                </button>

                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={clearScreening}
                                  disabled={readOnly || screeningLoading}
                                  title="Clear results"
                                >
                                  <i className="bi bi-x-lg" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        }

                        {groupedSanctionsReviewItems.length ? (
                          <>
                            <div
                              className={hideScreeningSummary ? undefined : "mt-3"}
                              onScroll={(event) => {
                                if (!sanctionsReviewHeaderSticky) {
                                  setSanctionsReviewScrolled(false);
                                  return;
                                }
                                const next = event.currentTarget.scrollTop > 2;
                                setSanctionsReviewScrolled((prev) =>
                                  prev === next ? prev : next
                                );
                              }}
                              style={{
                                maxHeight: 560,
                                overflowY: "auto",
                              }}
                            >
                              <motion.div
                                className="d-grid"
                                style={{ gap: 12 }}
                                layout="position"
                                transition={T.med}
                              >
                                <AnimatePresence initial={false}>
                                  {groupedSanctionsReviewItems.map((group, idx) => {
                                    if (needsFullName(group)) return (
                                      <ScreeningNameNotice key={group.key} subjects={[group]} readOnly={readOnly} />
                                    );
                                    const subjectWasScreened = hasSubjectScreening(screeningResult, group) ||
                                      (hasResult && group.matches.length > 0);
                                    if (!subjectWasScreened) return <ScreeningPendingSubject key={group.key} subject={group} readOnly={readOnly}
                                      disabled={screeningLoading} loading={runningSubjectKey === group.key}
                                      onRun={() => runSanctionsScreening(group)} />;

                                    const personDecision =
                                      personDecisions[group.key]?.decision;
                                    const decisionEditing =
                                      decisionEditKeys.has(group.key) || contextReviewChanged(group.key);
                                    const showDecisionChip =
                                      !!personDecision && !decisionEditing;
                                    const evidenceDecision = collapseReviewSnapshot
                                      ? collapseReviewSnapshot.personDecisions[group.key]?.decision
                                      : personDecision;
                                    const evidenceCollapsed = collapseReviewSnapshot
                                      ? !!evidenceDecision && !collapseReviewSnapshot.decisionEditKeys.has(group.key)
                                      : showDecisionChip;
                                    const hasSelectedEvidence =
                                      group.matches.some((match) => relevantKeys.has(match._key));
                                    const needsSupportingReason =
                                      evidenceDecision === "exposure" && !hasSelectedEvidence;
                                    const visiblePersonMatches = evidenceCollapsed
                                      ? group.matches.filter((match) =>
                                          relevantKeys.has(match._key)
                                        )
                                      : group.matches;
                                    const contextIcon = !personDecision ? userYellowIcon
                                      : personDecision === "exposure" ? userRedIcon : userGreenIcon;

                                    return (
                                      <motion.div
                                        key={group.key}
                                        className="rounded-3"
                                        layout="position"
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{
                                          opacity: 1,
                                          y: 0,
                                          transition: {
                                            ...T.fast,
                                            delay: Math.min(idx, 8) * 0.015,
                                          },
                                        }}
                                        exit={{ opacity: 0, y: 4, transition: T.fast }}
                                        style={{
                                          background: "#F9F7F5",
                                          color: "#4C5465",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: sanctionsReviewHeaderSticky
                                              ? "sticky"
                                              : "relative",
                                            top: 0,
                                            zIndex: sanctionsReviewHeaderSticky ? 3 : 1,
                                            background: "transparent",
                                          }}
                                        >
                                          {sanctionsReviewHeaderSticky ? (
                                            <>
                                              <span
                                                aria-hidden="true"
                                                style={{
                                                  position: "absolute",
                                                  top: 0,
                                                  left: 0,
                                                  width: 18,
                                                  height: 18,
                                                  background: "#fff",
                                                  pointerEvents: "none",
                                                  zIndex: 0,
                                                }}
                                              />
                                              <span
                                                aria-hidden="true"
                                                style={{
                                                  position: "absolute",
                                                  top: 0,
                                                  right: 0,
                                                  width: 18,
                                                  height: 18,
                                                  background: "#fff",
                                                  pointerEvents: "none",
                                                  zIndex: 0,
                                                }}
                                              />
                                            </>
                                          ) : null}
                                          <div
                                            className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                                            style={{
                                              position: "relative",
                                              zIndex: 1,
                                              margin: 0,
                                              padding: "16px",
                                              background: sanctionsReviewHeaderGlass
                                                ? "rgba(249, 247, 245, 0.72)"
                                                : "#F9F7F5",
                                              backdropFilter: sanctionsReviewHeaderGlass
                                                ? "blur(16px) saturate(1.14)"
                                                : "none",
                                              WebkitBackdropFilter:
                                                sanctionsReviewHeaderGlass
                                                  ? "blur(16px) saturate(1.14)"
                                                  : "none",
                                              borderTop:
                                                sanctionsReviewHeaderGlass
                                                  ? "1px solid rgba(222, 226, 230, 0.66)"
                                                  : "1px solid #DEE2E6",
                                              borderLeft:
                                                sanctionsReviewHeaderGlass
                                                  ? "1px solid rgba(222, 226, 230, 0.66)"
                                                  : "1px solid #DEE2E6",
                                              borderRight:
                                                sanctionsReviewHeaderGlass
                                                  ? "1px solid rgba(222, 226, 230, 0.66)"
                                                  : "1px solid #DEE2E6",
                                              borderTopLeftRadius: 8,
                                              borderTopRightRadius: 8,
                                              boxShadow: sanctionsReviewHeaderGlass
                                                ? "0 10px 26px rgba(45, 50, 60, 0.08)"
                                                : "none",
                                              transition:
                                                "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
                                            }}
                                          >
                                          <div
                                            className="d-flex gap-2"
                                            style={{
                                              alignItems: "center",
                                              minHeight: 58,
                                            }}
                                          >
                                            <span
                                              aria-hidden="true"
                                              className="d-inline-flex align-items-center justify-content-center"
                                              style={{
                                                width: 52,
                                                height: 58,
                                                flex: "0 0 52px",
                                                overflow: "visible",
                                              }}
                                            >
                                              <img
                                                src={contextIcon}
                                                alt=""
                                                aria-hidden="true"
                                                style={{
                                                  width: 50,
                                                  height: 50,
                                                  display: "block",
                                                  opacity: 1,
                                                  transform: "translateY(2px)",
                                                  transformOrigin: "center",
                                                }}
                                              />
                                            </span>
                                            <div
                                              className="d-flex flex-column justify-content-center"
                                              style={{ minHeight: 58 }}
                                            >
                                              <div
                                                className="fw-semibold"
                                                style={{ color: "#2f3441" }}
                                              >
                                                {titleCaseWords(group.name) ||
                                                  "Subject"}
                                              </div>

                                              {group.roles?.length ||
                                              !personDecision ? (
                                                <div className="d-flex gap-2 flex-wrap mt-2">
                                                  {group.roles.map((role) => (
                                                    <React.Fragment key={role}>
                                                      {roleBadge(role)}
                                                    </React.Fragment>
                                                  ))}
                                                  {!personDecision ? (
                                                    <span
                                                      className="squareBadge"
                                                      style={{
                                                        background: "#FEF9EE",
                                                        border:
                                                          "1px solid #DCD0A0",
                                                        color: "#7E6F3C",
                                                        fontWeight: 500,
                                                      }}
                                                    >
                                                      Awaiting review
                                                    </span>
                                                  ) : null}
                                                </div>
                                              ) : null}
                                            </div>
                                          </div>

                                          <div className="d-flex flex-column align-items-end justify-content-center align-self-stretch" style={{ gap: 4 }}>
                                            {/* Person-level FINAL OUTCOME of this person's review.
                                                Question state: caption + amber cue + dashed empty pair.
                                                Verdict state: one stamped chip + a deliberate "Change". */}
                                            <div className="d-flex align-items-center gap-2">
                                            <div
                                              className={`d-flex align-items-center gap-3${!showDecisionChip ? " kycPepReviewOutcomePending" : ""}`}
                                              style={{
                                                background: showDecisionChip ? "#FCFAF8" : "#FEF9EE",
                                                border: "1px solid #E7E2DD",
                                                borderRadius: 10,
                                                padding: "8px 12px",
                                              }}
                                            >
                                              <div
                                                className="fw-semibold text-muted"
                                                style={{
                                                  fontSize: 11.5,
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                Review outcome
                                              </div>
                                              <div
                                                aria-hidden="true"
                                                style={{
                                                  width: 1,
                                                  alignSelf: "stretch",
                                                  background: "#E7E2DD",
                                                }}
                                              />
                                              <div className="d-flex flex-column align-items-end gap-1">
                                              <div
                                                id={!showDecisionChip && hasSelectedEvidence ? `sanctions-evidence-guidance-${sectionNo}-${group.key}` : undefined}
                                                className="text-muted"
                                                style={{ fontSize: 11.5 }}
                                              >
                                                {showDecisionChip
                                                  ? isSubjectReviewed(group.key)
                                                    ? "Evidence below has been reviewed"
                                                    : "Add supporting information to complete review"
                                                  : hasSelectedEvidence
                                                  ? "Confirm sanctions exposure or unselect hits"
                                                  : "Review the evidence below, then select"}
                                              </div>

                                              {showDecisionChip ? (
                                                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                                  <span
                                                    className="badge badgeStandard"
                                                    role={readOnly ? undefined : "button"}
                                                    title={
                                                      readOnly
                                                        ? undefined
                                                        : "Click to change"
                                                    }
                                                    onClick={
                                                      readOnly
                                                        ? undefined
                                                        : () =>
                                                            openDecisionEdit(
                                                              group.key
                                                            )
                                                    }
                                                    style={{
                                                      background:
                                                        personDecision ===
                                                        "exposure"
                                                          ? "#D69DAC"
                                                          : "#9bb0a5",
                                                      color: "#fff",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      gap: 6,
                                                      height: 26,
                                                      padding: "0 10px",
                                                      cursor: readOnly
                                                        ? "default"
                                                        : "pointer",
                                                    }}
                                                  >
                                                    {personDecision === "exposure"
                                                      ? "Sanctions exposure"
                                                      : "No sanctions exposure"}
                                                    {!readOnly ? (
                                                      <i
                                                        className="bi bi-pencil"
                                                        aria-hidden="true"
                                                        style={{ fontSize: 10, opacity: 0.85 }}
                                                      />
                                                    ) : null}
                                                  </span>
                                                </div>
                                              ) : (
                                                <div className="d-flex align-items-center flex-nowrap justify-content-end" style={{ gap: 4 }}>
                                                  <div
                                                    className="btn-group"
                                                    role="group"
                                                    aria-label="Person-level Sanctions final outcome"
                                                  >
                                                    <button
                                                      type="button"
                                                      className={`btn btn-sm ${
                                                        personDecision ===
                                                        "exposure" && !decisionEditing
                                                          ? "btn-toggle-on-lightgrey"
                                                          : "btn-toggle-off-lightgrey"
                                                      }`}
                                                      style={{
                                                        height: 26,
                                                        padding: "0 10px",
                                                        fontSize: 12,
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        ...(personDecision ===
                                                        "exposure"
                                                          ? {
                                                              background: decisionEditing ? "#F8EFF2" : "#D69DAC",
                                                              borderColor: decisionEditing ? "#D9D9D9" : "#D69DAC",
                                                              color: decisionEditing ? "#6C757D" : "#fff",
                                                            }
                                                          : !personDecision
                                                          ? {
                                                              borderStyle:
                                                                "dashed",
                                                              borderColor:
                                                                "#B9BFC7",
                                                              background:
                                                                "#FFFFFF",
                                                            }
                                                          : {}),
                                                      }}
                                                      disabled={readOnly}
                                                      aria-pressed={personDecision === "exposure"}
                                                      onClick={() =>
                                                        setPersonDecision(
                                                          group.key,
                                                          "exposure"
                                                        )
                                                      }
                                                    >
                                                      Sanctions exposure
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className={`btn btn-sm kycNoExposureOption ${
                                                        personDecision ===
                                                        "no_exposure" && !decisionEditing
                                                          ? "btn-toggle-on-lightgrey"
                                                          : "btn-toggle-off-lightgrey"
                                                      }`}
                                                      style={{
                                                        height: 26,
                                                        padding: "0 10px",
                                                        fontSize: 12,
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        ...(personDecision ===
                                                        "no_exposure"
                                                          ? {
                                                              background: decisionEditing ? "#F0F5F2" : "#9bb0a5",
                                                              borderColor: decisionEditing ? "#D9D9D9" : "#9bb0a5",
                                                              color: decisionEditing ? "#6C757D" : "#fff",
                                                            }
                                                          : !personDecision
                                                          ? {
                                                              borderStyle:
                                                                "dashed",
                                                              borderColor:
                                                                "#B9BFC7",
                                                              background:
                                                                "#FFFFFF",
                                                            }
                                                          : {}),
                                                      }}
                                                      disabled={readOnly || hasSelectedEvidence}
                                                      aria-describedby={!showDecisionChip && hasSelectedEvidence ? `sanctions-evidence-guidance-${sectionNo}-${group.key}` : undefined}
                                                      aria-pressed={personDecision === "no_exposure"}
                                                      onClick={() =>
                                                        setPersonDecision(
                                                          group.key,
                                                          "no_exposure"
                                                        )
                                                      }
                                                    >
                                                      No sanctions exposure
                                                    </button>
                                                  </div>
                                                  {decisionEditing && personDecision && !readOnly ? (
                                                    <button
                                                      type="button"
                                                      className="btn btn-sm text-muted p-0 d-inline-flex align-items-center justify-content-center"
                                                      aria-label={`Clear review outcome for ${group.name}`}
                                                      style={{ width: 18, height: 18, flexShrink: 0, border: "1px solid #D9D9D9", borderRadius: "50%", background: "transparent" }}
                                                      onClick={() => setPersonDecision(group.key, null)}
                                                    >
                                                      <i className="bi bi-x-lg" aria-hidden="true" style={{ fontSize: 8, lineHeight: 1 }} />
                                                    </button>
                                                  ) : null}
                                                </div>
                                              )}
                                              </div>
                                            </div>
                                            </div>
                                          </div>
                                        </div>
                                        </div>

                                        <div
                                          style={{
                                            marginTop: 0,
                                            padding:
                                              evidenceCollapsed
                                                ? hasSelectedEvidence || needsSupportingReason || clean(sanctions?.supportingInformation?.[group.key])
                                                  ? "0 16px 20px"
                                                  : "0 16px 1px"
                                                : "0 16px 16px",
                                            borderLeft: "1px solid #DEE2E6",
                                            borderRight: "1px solid #DEE2E6",
                                            borderBottom: "1px solid #DEE2E6",
                                            borderBottomLeftRadius: 8,
                                            borderBottomRightRadius: 8,
                                            background: "#F9F7F5",
                                          }}
                                        >
                                        {contextReviewChanged(group.key) ? <div className="identityContextMissing" role="status">KYC context has changed. Review the updated comparisons and confirm your outcome.</div> : null}
                                        <MissingContextNote meta={group.meta} roles={group.roles} readOnly={readOnly} />
                                        <div>
                                          <AnimatePresence initial={false}>
                                          {visiblePersonMatches.length > 0 ? (
                                            <motion.div
                                              key="watchlist-evidence"
                                              {...evidenceCollapse}
                                              style={{ overflow: "hidden" }}
                                            >
                                              <div
                                                className="text-muted"
                                                style={{
                                                  fontFamily: '"Nunito", sans-serif',
                                                  fontWeight: 500,
                                                  fontSize: 13,
                                                  marginTop: 12,
                                                  marginBottom: 6,
                                                  borderTop: "1px solid rgba(121, 141, 160, 0.16)",
                                                  paddingTop: 10,
                                                }}
                                              >
                                                Potential matches · Watchlist databases
                                              </div>
                                              <div className="list-group" style={{ gap: 20 }}>
                                                  <AnimatePresence initial={false}>
                                                    {visiblePersonMatches.map(
                                                      (match) => (
                                                        <motion.div
                                                          key={match._key}
                                                          {...evidenceCollapse}
                                                          style={{
                                                            overflow: "hidden",
                                                            borderRadius: "var(--bs-list-group-border-radius, 0.375rem)",
                                                          }}
                                                        >
                                                          {renderSanctionsMatchRow(match)}
                                                        </motion.div>
                                                      )
                                                    )}
                                                  </AnimatePresence>
                                                </div>
                                            </motion.div>
                                          ) : null}
                                          {!screeningLoading && !screeningErr && group.matches.length === 0 && !screeningResult?.errors?.some(error => error.key === group.key || error.name === group.name) ? (
                                            <div className="small text-muted" style={{ padding: "10px 0" }}>No potential sanctions matches found</div>
                                          ) : null}
                                          {!evidenceCollapsed || needsSupportingReason || clean(sanctions?.supportingInformation?.[group.key]) ? (
                                          <motion.div
                                            key="supporting-information"
                                            {...evidenceCollapse}
                                            style={{ overflow: "hidden" }}
                                          >
                                          <label
                                            htmlFor={`sanctions-supporting-${sectionNo}-${group.key}`}
                                            className="d-block text-muted"
                                            style={{ fontFamily: '"Nunito", sans-serif', fontWeight: 500, fontSize: 13, marginTop: 12, marginBottom: 6 }}
                                          >
                                            Other supporting information
                                          </label>
                                          <div className="rounded-3 border bg-white p-3">
                                            <textarea
                                              id={`sanctions-supporting-${sectionNo}-${group.key}`}
                                              aria-label={`Other supporting information for ${group.name}`}
                                              className="form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line"
                                              rows={1}
                                              required={needsSupportingReason}
                                              placeholder={showDecisionChip && needsSupportingReason ? "No hit were flagged. Explain why this subject has sanctions exposure" : undefined}
                                              ref={(element) => autoResizeTextarea(element)}
                                              readOnly={readOnly}
                                              value={sanctions?.supportingInformation?.[group.key] || ""}
                                              onChange={(event) => {
                                                if (readOnly) return;
                                                setSupportingInformation(group.key, event.target.value);
                                                autoResizeTextarea(event.currentTarget);
                                              }}
                                            />
                                          </div>
                                          </motion.div>
                                          ) : null}
                                          </AnimatePresence>
                                        </div>
                                        </div>
                                      </motion.div>
                                    );
                                  })}
                                </AnimatePresence>
                              </motion.div>
                            </div>

                          </>
                        ) : (
                          <div className="small mt-3">
                            No screening evidence to review yet.
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
        {/* --- END --- */}
      </div>
      {/* -- end main card -- */}
    </div>
  );
});

export default KycSectionSanctionsWithScreening;
